/**
 * BỘ ĐIỀU KHIỂN GAME — nối mô phỏng (sim) với giao diện (DOM + Phaser).
 * Một ví vàng duy nhất: meta.gold ⇄ world.gold.
 */
import Phaser from 'phaser';
import { ArenaScene } from './scenes/arena-scene';
import { ArenaBattleScene } from './scenes/arena-battle-scene';
import { ArenaWorld } from './sim/arena/world';
import { ARENA_MAX_FARMERS, ARENA_TICK } from './data/arena';
import { addArenaResource, arenaHeroClasses, recordArenaResult } from './sim/arena-meta';
import type { ArenaSnapshot } from './ui/hud-arena';
import { isClassHero, World, MAX_HEROES_ON_FIELD, START_RUN_GOLD, type LineupEntry, type SimEvent } from './sim/world';
import { loadMeta, saveMeta, resetSave } from './save/storage';
import { loadArtExt } from './art/art-url';
import { loadWardrobe } from './data/wardrobe';
import { onLangChange, t } from './i18n';
import { beginTask, setProgress } from './ui/loading';
import { className, diffName, enemyName, mapName, skillName, stageBossName } from './i18n/loc';
import { simMsg } from './i18n/sim-messages';
import {
  MAX_ROSTER,
  buyHero,
  buyPotion,
  heroPrice,
  sellClassShards,
  unlockClassWithShards,
  upgradeClassWithShards,
  classStatsFor,
  startTimeRoom,
  stepTimeRoom,
  trainClass,
  trainCost,
  type MetaState,
  type TimeRoomSession,
} from './sim/meta';
import { CLASSES, CLASS_ORDER, type ClassId } from './data/classes';
import type { Unit } from './sim/types';
import { POTIONS, type PotionKind } from './data/items';
import { Hud } from './ui/hud';
import { pickShardTargets } from './data/maps';
import { skillFormById, skillFormCounts } from './data/wardrobe';
import {
  DIFFICULTIES,
  FIRST_MAP_ID,
  isUnlocked,
  mapById,
  mapKey,
  nextMap,
  rewardFor,
  rollRewards,
  type Difficulty,
} from './data/maps';
import { applyReward, markCleared, nextObjective, progressGrid, type MapProgress } from './sim/progress';
import { equippedSkills } from './sim/craft';
import type { SkillDef } from './data/classes';
import { skillById } from './data/skills-extra';

/**
 * NHÃN của món "ĐỒ HOÀN CHỈNH" trong banner thưởng.
 *
 * `RolledRewards.fullItemId` (xem `data/maps.ts`) hoặc là **id CHIÊU** hoặc là **id CLASS** tuỳ
 * loại món trúng. Trước đây banner in thẳng id thô nên hiện ra một chuỗi id giữa giao diện.
 * Tra theo id rồi trả TÊN đã dịch.
 */
function fullItemLabel(id: string | undefined): string {
  if (!id) return '';
  if (id in CLASSES) return className(id as ClassId);
  const sk = skillById(id);
  return sk ? skillName(sk) : id;
}


export class Game {
  meta: MetaState;
  world: World | null = null;
  scene!: ArenaScene;
  hud!: Hud;
  /**
   * TƯỚNG ĐANG CHỌN — đi qua SETTER để con thoi trên đầu luôn khớp.
   *
   * Vì sao không để field trần: có 5 chỗ đặt `selectedHeroId` (bấm thẻ tướng, bấm trên sân,
   * mua tướng, sang màn, nút "Chọn" trong tab). Nếu mỗi chỗ phải tự nhớ cập nhật con thoi thì
   * sớm muộn có chỗ quên ⇒ con thoi chỉ sai tướng. Gom vào một setter là hết lối quên.
   */
  /**
   * SỐ THỨ TỰ TƯỚNG MUA TRONG TRẬN — dùng để sinh `heroId` duy nhất (`run-<class>-<n>`).
   * Sổ tướng đã xoá nên id không còn đến từ save; mỗi màn đếm lại từ 0 (xem `startLevel`).
   */
  private runHeroSeq = 0;

  private _selectedHeroId: string | null = null;

  get selectedHeroId(): string | null {
    return this._selectedHeroId;
  }

  set selectedHeroId(id: string | null) {
    this._selectedHeroId = id;
    this.syncSelectMarker();
  }

  /**
   * Đẩy id đơn vị của tướng đang chọn sang tầng cảnh để vẽ CON THOI trên đầu.
   * Gọi lại mỗi khi đổi lựa chọn; nếu tướng chưa có trên sân (mới mua/ chưa vào trận) thì
   * để `null` — con thoi không chỉ vào ai cả, đúng như mong đợi.
   */
  private syncSelectMarker(): void {
    if (!this.scene) return;
    this.scene.selectedHeroUnitId = this.selectedUnitId();
  }
  timeRoom: TimeRoomSession | null = null;

  /**
   * CLASS ĐANG CHỌN Ở PHÒNG THỜI GIAN (panel này nay nằm ở TRANG CHỦ và luyện theo class —
   * người dùng chốt: *"Sức mạnh trong phòng thời gian chuyển ra ngoài màn hình chính, gắn theo
   * class giống training"*).
   */
  timeClass: ClassId = 'strength';

  /**
   * TAB CON ĐANG MỞ trong menu TẬP LUYỆN — người dùng chốt *"Để cùng trong menu tập luyện nhưng
   * chia tab ra"*: `level` = Võ Đường (trả vàng theo cấp), `time` = Phòng Thời gian (trả vàng theo
   * giây ⇒ `classPower`). Cả hai đều tác động lên CLASS chứ không phải từng nhân vật.
   */
  trainTab: 'level' | 'time' = 'level';
  /** tab đang mở ở sidebar */
  /**
   * TAB ĐANG MỞ trong sidebar/ panel trang chủ.
   * `bag` = TÚI ĐỒ (mảnh class: mở khoá · nâng cấp · bán) — người dùng yêu cầu bổ sung ở màn chính.
   */
  tab: 'roster' | 'gear' | 'train' | 'time' | 'skill' | 'shop' | 'bag' = 'roster';
  /*
   * ⚠️ FIELD `wardrobeTab` ĐÃ BỊ XOÁ (cùng tab "Vào sân" trong tab Trang phục).
   * Người dùng: *"Bỏ nút `Vào sân` ở phần trang phục, sắp xếp lại các nút cho gọn"*.
   * Tab Trang phục nay CHỈ còn phần chọn dáng theo class (`renderGear()` trong `ui/hud.ts`),
   * nên một field phân nhánh chỉ có đúng 1 giá trị là mã chết.
   * Chọn tướng đang điều khiển vẫn còn: bấm THẺ TƯỚNG trong màn chiến đấu (`selectedHeroId`).
   * (Trước đây field này là `gearSlot` — 5 slot phụ kiện; tab Trang phục nay chọn theo SKILL.)
   */
  /** class đang xem khi mua tướng */
  shopClass: ClassId = 'strength';
  /**
   * NGƯỜI CHƠI ĐÃ BẤM "VÀO TRẬN" CHƯA.
   * Yêu cầu người dùng: chưa bấm thì nút "Bắt đầu Wave" KHÔNG được chọn — tránh cảnh
   * vừa vào đã bấm wave trước khi kịp đọc luật rồi quái tràn vào Nhà.
   */
  started = false;
  /** tự vào wave tiếp theo sau khi hết wave (đếm lùi 3 giây) */
  autoNextWave = false;
  /** tự dùng tuyệt kỹ (bot niệm theo TẦM CỦA TỪNG CHIÊU — xem `World.skillReach`) */
  autoSkill = true;
  /** giây đếm lùi còn lại trước khi tự vào wave; null = không đếm */
  startCountdown: number | null = null;

  private phaser!: Phaser.Game;
  private timeRoomTimer: number | null = null;
  private lastTimeRoomTick = 0;
  private saveTimer: number | null = null;
  private countdownTimer: number | null = null;

  constructor() {
    this.meta = loadMeta();
    this.loadPrefs();
    this.phaser = new Phaser.Game({
      type: Phaser.AUTO,
      parent: 'arena',
      width: 1000,
      height: 600,
      backgroundColor: '#120d0a',
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      input: {
        touch: true,
        activePointers: 3,
      },
      render: { antialias: true, roundPixels: false },
      /*
       * ⚠️⚠️ ĐĂNG KÝ SCENE ARENA — KEY MỚI `'arena-battle'`, KHÔNG PHẢI `'arena'`.
       *
       * Người dùng nhấn mạnh: **Arena là chế độ BỔ SUNG, KHÔNG thay thế game 10-wave.** `ArenaScene`
       * (key `'arena'`) là trận ĐANG CHẠY PRODUCTION; `this.scene = phaser.scene.getScene('arena')`
       * ở `onBoot` phải trả về ĐÚNG nó. Thêm `ArenaBattleScene` với key riêng ⇒ Phaser có 2 scene
       * độc lập, `getScene('arena')` vẫn trỏ đúng scene cũ, và game cũ không đổi hành vi một chút nào.
       *
       * ⚠️ CHỈ ĐĂNG KÝ, KHÔNG `start`: scene ở trạng thái ngủ cho tới khi người chơi bấm nút Arena
       * (xem `startArenaFromHome`). Đăng ký mà auto-start là Phaser chạy 2 scene cùng lúc ⇒ hai
       * vòng lặp cùng vẽ vào một canvas và cùng nhận `pointerdown`.
       *
       * ⚠️ `ArenaBattleScene` xếp SAU `ArenaScene` và KHÔNG `active` — nếu để nó tự chạy thì
       * `ArenaScene.update` (trận 10-wave) vẫn chạy song song ở dưới và ta có HAI mô phỏng cùng
       * tiêu CPU, chưa kể hai lớp vẽ chồng lên nhau.
       */
      scene: [ArenaScene, ArenaBattleScene],
      callbacks: {
        postBoot: () => this.onBoot(),
      },
    });
  }

  /** Callback chạy khi boot xong (dùng cho màn chờ tắt đúng lúc). */
  private readyCbs: Array<() => void> = [];

  /**
   * ĐĂNG KÝ NHẬN BIẾT BOOT XONG.
   *
   * ⚠️ Có cơ chế này vì `new Game()` KHÔNG boot đồng bộ (Phaser boot bất đồng bộ, `postBoot` mới
   * chạy `onBoot`). Ai cần "sau khi game sẵn sàng" mà chỉ `await new Game()` sẽ chạy TRƯỚC khi HUD
   * tồn tại ⇒ lỗi `Cannot read properties of undefined`.
   */
  onReady(cb: () => void): void {
    if (this.ready) cb();
    else this.readyCbs.push(cb);
  }

  private ready = false;

  /**
   * PROMISE ẢNH AI CỦA TRẬN — dùng để vào trận biết art đã sẵn sàng chưa.
   *
   * Người dùng: *"khi vào game, load xong ảnh mới hiện màn hình chiến đấu"*. Ảnh AI nạp MỘT LẦN cho
   * cả phiên (trong `onBoot`) nên chỉ trận ĐẦU có thể còn đang nạp; các trận sau tới đây là đã xong
   * ⇒ không hiện màn chờ thừa.
   */
  private artReady: Promise<void> | null = null;

  /** Ảnh AI của trận đã nạp xong chưa (để UI biết có cần hiện màn chờ không). */
  isArtReady(): boolean {
    return this.artDone;
  }

  /**
   * Chờ tới khi ảnh AI nạp xong. ⚠️ KHÔNG BAO GIỜ ném lỗi: `preloadAll()` đã bắt lỗi từng ảnh và
   * `.catch()` bên trên đã nuốt lỗi tổng ⇒ hàm này luôn resolve, người chơi không bao giờ kẹt ở màn
   * chờ vì 1 ảnh hỏng.
   */
  awaitArtReady(): Promise<void> {
    return this.artReady ?? Promise.resolve();
  }

  private artDone = false;

  private async onBoot(): Promise<void> {
    this.scene = this.phaser.scene.getScene('arena') as ArenaScene;
    this.scene.hooks = {
      onWorldEvent: (e) => this.onWorldEvent(e),
      onStatusChange: (s) => this.onStatusChange(s),
      onCommandMove: (x, y) => this.commandMove(x, y),
      /*
       * BẤM VÀO TƯỚNG TRÊN SÂN = CHỌN TƯỚNG ĐÓ.
       * Yêu cầu người dùng: "click vào hero nào thì sẽ là chọn hero đó thay vì bắt buộc phải
       * chọn ở mục bên dưới". Trả `true` để cảnh KHÔNG ra lệnh di chuyển cho cú bấm đó.
       */
      onPickHero: (unitId) => this.pickHeroOnField(unitId),
    };
    this.hud = new Hud(this);
    // đổi ngôn ngữ ⇒ cập nhật cả chữ do Phaser vẽ (tên tướng/ quái, nhãn Nhà)
    onLangChange(() => this.setLanguage());
    /*
     * ĐỌC ĐUÔI ẢNH AI TRƯỚC KHI NẠP BẤT CỨ ẢNH NÀO.
     *
     * Vì sao phải đứng TRƯỚC `setForms`/`preloadAll`: `sync-art.mjs` nén ảnh gốc sang
     * `.webp`, nhưng khi chưa cài `sharp` thì nó copy nguyên `.png`. Đuôi thật được ghi
     * vào `public/art/ai/.ext`. Mọi hàm dựng đường dẫn (`artUrl`, `artPartsUrl`) đọc giá
     * trị này ⇒ PHẢI nạp xong trước, nếu không ảnh 404 và game im lặng lùi về art vector.
     * Xem `src/art/art-url.ts`.
     */
    await loadArtExt();
    /*
     * NẠP DANH MỤC DÁNG **TRƯỚC** KHI HUD VẼ LẦN ĐẦU.
     *
     * ⚠️ BẪY CỦA LẦN ĐỔI NÀY: `SKILL_FORMS` trước kia do `import.meta.glob` chốt LÚC BUILD
     * nên luôn sẵn sàng ngay. Nay danh mục đọc từ `manifest.json` LÚC CHẠY ⇒ nếu HUD vẽ
     * trước khi nạp xong thì tab Trang phục hiện **"0 dáng"** dù ảnh có thật — đúng kiểu
     * lỗi im lặng khó đoán nhất.
     * Chờ ở đây (màn Loading đang hiện) là chỗ duy nhất chắc chắn đứng trước mọi lần vẽ.
     */
    await loadWardrobe();
    /*
     * ĐẨY DÁNG ĐANG MẶC XUỐNG TẦNG HÌNH TRƯỚC KHI NẠP ẢNH.
     * `preloadAll()` nạp luôn ảnh dáng đang mặc (mỗi class ≤ 1 ảnh) — nếu đặt sau thì trận đầu
     * tiên sẽ dựng hình bằng dáng gốc rồi mới đổi, nhìn thấy rõ.
     */
    this.scene.units.setForms(this.meta.formByClass);
    /*
     * NẠP ẢNH AI KHÔNG CHẶN GAME.
     *
     * ⚠️ BẪY ĐÃ SẬP (đo bằng Chrome thật): chờ `await preloadAll()` trước khi hiện
     * banner làm boot mất **39 giây** (sau khi gom lô refresh còn ~13 giây) — người
     * chơi nhìn màn hình trắng và tưởng game hỏng. Ảnh AI chỉ là "da đẹp", không
     * được phép chặn giao diện.
     *
     * Cách làm: vào game ngay bằng art vector, nạp ảnh ở nền, xong thì DỰNG LẠI view
     * ⇒ người chơi thấy ảnh AI trước khi bấm "Bắt đầu Wave" (lúc đó mới có quái).
     */
    /*
     * ⚠️ ĐỔI HÀNH VI THEO YÊU CẦU NGƯỜI DÙNG (trước đây nạp ở NỀN, không chặn).
     *
     * Người dùng: *"khi chiến đấu, khi vào trận hình nhân vật, sân,.. đôi lúc load lên chậm, có thể
     * do tốc độ mạng. Vậy có cách nào khắc phục không? Nếu không được thì thêm màn hình Load… khi
     * vào game, load xong ảnh mới hiện màn hình chiến đấu"*.
     *
     * Vì sao "khắc phục bằng cách khác" KHÔNG khả thi: ảnh AI là ảnh thật tải qua mạng, không thể
     * nhanh hơn tốc độ mạng. Cách duy nhất để người chơi KHÔNG thấy cảnh "art vector rồi đổi sang
     * ảnh AI" là CHỜ — nên màn chờ là giải pháp đúng.
     *
     * Vẫn giữ `startLevel()` chạy SONG SONG (không `await` trước nó) để logic trận sẵn sàng trong lúc
     * nạp ảnh; chỉ có VIỆC HIỆN MÀN HÌNH bị màn chờ giữ lại.
     */
    const artDone = beginTask();
    setProgress(0, t('Đang nạp hình nhân vật…', 'Loading character art…'));
    this.artReady = this.scene.units
      .preloadAll((done, total, label) => {
        setProgress((done / Math.max(1, total)) * 100, t('Đang nạp {label}…', 'Loading {label}…', { label }));
      })
      .then(() => {
        this.scene.units.reset(); // buộc dựng lại rig ⇒ lần này dùng ảnh AI
        console.log('[art] sẵn sàng — nhân vật/quái dùng ảnh AI');
      })
      .catch((err) => console.error('[art] nạp ảnh AI thất bại — chơi bằng art vector:', err))
      .finally(() => {
        this.artDone = true;
        artDone();
      });
    this.startLevel();
    this.showIntro();
    // boot xong ⇒ báo cho màn chờ (xem `onReady`)
    this.ready = true;
    for (const cb of this.readyCbs) cb();
    this.readyCbs = [];
    /*
     * ⚠️ NGỦ VÒNG LẶP SAU KHI BOOT XONG.
     * `showIntro()` → `showScreen('home')` đã gọi `setLoopAwake(false)`, nhưng lúc đó Phaser có
     * thể CHƯA boot xong (`this.phaser.loop` chưa sẵn sàng) nên lệnh ngủ bị bỏ qua, và vòng lặp
     * khởi động lại ⇒ vẫn vẽ 30 khung/giây sau lưng trang chủ ⇒ khựng khi miết (đo được: vẫn
     * `loop.running === true` sau khi vào nhà).
     * Gọi lại ở đây, và hẹn thêm 2 nhịp nữa cho chắc (boot của Phaser là bất đồng bộ).
     */
    if (this.hud.isHome) {
      this.setLoopAwake(false);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (this.hud.isHome) this.setLoopAwake(false);
        });
      });
    }
  }

  // ------------------------------------------------------------ vòng đời màn

  /**
   * TUỲ CHỌN CỦA NGƯỜI CHƠI (auto wave · auto chiêu) — lưu riêng ở `localStorage`.
   *
   * Vì sao không nhét vào `meta` (save chính): save có phiên bản + migrate, thêm trường
   * vào đó là phải bump phiên bản cho một thứ chẳng liên quan tới tiến trình chơi.
   * Âm thanh cũng đang lưu kiểu này (`tdkungfu.sound`).
   */
  private loadPrefs(): void {
    try {
      const raw = localStorage.getItem('tdkungfu.prefs');
      if (!raw) return;
      const p = JSON.parse(raw) as { autoNextWave?: boolean; autoSkill?: boolean };
      this.autoNextWave = p.autoNextWave === true;
      this.autoSkill = p.autoSkill !== false;
    } catch {
      /* localStorage hỏng/ bị chặn ⇒ dùng mặc định */
    }
  }

  private savePrefs(): void {
    try {
      localStorage.setItem('tdkungfu.prefs', JSON.stringify({ autoNextWave: this.autoNextWave, autoSkill: this.autoSkill }));
    } catch {
      /* bỏ qua */
    }
  }

  /**
   * Bấm "Vào trận" ở sảnh chọn màn ⇒ DỰNG LẠI màn theo đúng màn/ độ khó vừa chọn,
   * rồi mở khoá nút Bắt đầu Wave.
   *
   * ⚠️ LỖI ĐÃ SỬA (đo được bằng trình duyệt thật): boot gọi `startLevel()` NGAY để có sân
   * mà xem, lúc đó `currentMap` mới là mặc định '1.1'. Nếu "Vào trận" chỉ bật cờ `started`
   * thì sân đang chơi vẫn là sân dựng cho 1.1 ⇒ chọn màn 1.2 (stageIndex 1) mà quái vẫn
   * nhẹ như 1.1, và hệ số độ khó easy/normal/elite cũng bị bỏ qua (`diffMult` chỉ áp lúc
   * tạo World). Đo trước khi sửa: `meta.stageIndex = 1` nhưng `world.stageIndex = 0`.
   * Nay: chọn màn xong ⇒ dựng lại đúng màn đó.
   */
  /**
   * TRẬN ĐÃ THỰC SỰ BẮT ĐẦU CHƯA — dùng cho nút Back.
   *
   * Người dùng: *"khi ấn nút back mà game đã start thì phải hỏi có muốn thoát không"*.
   * ⚠️ Không dùng `started` trần: cờ đó bật ngay khi `beginRun()` chạy, kể cả lúc người chơi mới
   * "chuẩn bị" (chưa bấm Bắt đầu Wave) — lúc đó thoát KHÔNG mất gì, hỏi sẽ phiền.
   * Chỉ hỏi khi ĐÃ ĐÁNH: wave > 0 (đã qua wave 1) hoặc đang giữa wave.
   */
  hasStartedRun(): boolean {
    if (!this.started || !this.world) return false;
    return this.world.wave > 0;
  }

  /**
   * THOÁT TRẬN, XOÁ TIẾN TRÌNH TRẬN.
   *
   * Người dùng: *"nếu có thì clear tiến trình và quay lại trang chủ"*.
   *
   * ⚠️ PHẠM VI XOÁ — chỉ tiến trình CỦA TRẬN: world hiện tại (wave, quái, vàng ví trận, bộ chiêu
   * đang chạy). KHÔNG đụng tiến trình TRANG CHỦ (`meta.gold`, `classShards`, `unlocked`, `training`,
   * `classPower`) — người chơi thoát trận không được mất công cày ở nhà.
   */
  abandonRun(): void {
    this.world = null;
    this.started = false;
    /*
     * Dựng lại sân TRỐNG để trận sau bắt đầu sạch. `startLevel()` tạo `World` mới nên không còn
     * dấu vết wave/ quái/ vàng của trận vừa bỏ. Gọi ở đây để về trang chủ xong là sân đã sẵn sàng,
     * không phải dựng lại lúc bấm "VÀO TRẬN" (tránh khựng hình).
     */
    this.startLevel();
    this.hud.render();
  }

  beginRun(): void {
    /*
     * ⚠️ CHỈ DỰNG LẠI SÂN KHI CẦN.
     *
     * `startLevel()` tạo World MỚI ⇒ wave đang đánh bị xoá về `prep`. Nếu người chơi vào trận,
     * đang đánh dở rồi bấm 🏠 về trang chủ (xem Shop/ Tập luyện) và bấm "VÀO TRẬN" lại thì
     * mất sạch tiến độ wave — mà họ chỉ định quay lại trận đang chơi.
     *
     * Vì vậy: đã có sân và trận đã bắt đầu ⇒ chỉ QUAY LẠI, không dựng lại. Chỉ dựng lại khi
     * chưa vào trận, hoặc khi màn/ độ khó vừa được đổi (lúc đó `world` đã khác màn).
     */
    const needRebuild = !this.world || !this.started || this.world.stageIndex !== this.currentMapDef().stageIndex;
    this.started = true;
    if (needRebuild) this.startLevel();
    this.hud.render();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ARENA PVE — CHẾ ĐỘ BỔ SUNG (màn thứ ba) · KHÔNG thay thế game 10-wave
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * SCENE ARENA (key `'arena-battle'`) — KHÁC `this.scene` (key `'arena'`, trận 10-wave).
   *
   * ⚠️ VÌ SAO HAI SCENE TỒN TẠI SONG SONG MÀ KHÔNG XUNG ĐỘT: `this.scene` giữ trận TD; scene này
   * chỉ chạy khi người chơi bấm nút Arena. Vào Arena thì `ArenaScene` bị TẠM DỪNG (xem
   * `startArenaFromHome`) — nếu không, hai `update` cùng chạy và cùng nhận `pointerdown` trên MỘT
   * canvas: cú bấm chọn land sẽ đồng thời ra lệnh di chuyển cho tướng TD.
   */
  private arenaScene: ArenaBattleScene | null = null;

  /** Lấy scene Arena, chỉ khi nó THẬT SỰ đã được Phaser dựng xong. */
  private arena(): ArenaBattleScene | null {
    if (!this.arenaScene) {
      const s = this.phaser?.scene?.getScene('arena-battle') as ArenaBattleScene | undefined;
      // `getScene` trả `null`/ ném nếu scene chưa được thêm ⇒ coi như chưa sẵn sàng, KHÔNG ném tiếp
      if (!s || typeof s.attachWorld !== 'function') return null;
      this.arenaScene = s;
    }
    return this.arenaScene;
  }

  /**
   * CLASS ĐƯỢC RA QUÂN Ở ARENA — xem `arenaHeroClasses()` trong `src/sim/arena-meta.ts`.
   *
   * ⚠️ MỘT HÀM, HAI CHỖ GỌI (`startArenaFromHome` truyền cho sim, `renderArenaHud` cho HUD): nếu
   * mỗi chỗ tự ghép danh sách thì HUD và sim có thể LỆCH — HUD hiện nút mà `summonHero` của sim từ
   * chối (`Chưa mở khoá …`), hoặc ngược lại: có class ra quân được mà HUD không cho bấm.
   *
   * ⚠️ TRUYỀN CẢ `meta.unlocked` (tiến trình TD) LẪN `meta.arena.unlockedHeroes`: người chơi đã mở
   * class ở TD thì phải ra quân được ở Arena luôn — xem chú thích dài ở `arenaHeroClasses`.
   */
  private arenaHeroClasses(): ClassId[] {
    return arenaHeroClasses(this.meta.arena, this.meta.unlocked, Object.keys(CLASSES)) as ClassId[];
  }

  /**
   * MỞ ARENA TỪ NÚT Ở TRANG CHỦ — điểm vào DUY NHẤT của chế độ Arena.
   *
   * Gồm 4 việc, theo ĐÚNG thứ tự này:
   *   1. đổi màn hình trước (`showScreen('arena')`) để `#arena` có kích thước thật;
   *   2. dựng `ArenaWorld` (sim thuần) rồi giao cho scene — scene KHÔNG tự dựng world, đúng như
   *      `ArenaBattleScene.attachWorld` đã ghi: nhờ vậy sim vẫn chạy headless cho công cụ cân bằng;
   *   3. tạm dừng `ArenaScene` (trận TD) — xem chú thích `arenaScene`;
   *   4. ép Phaser đo lại kích thước (vòng `requestAnimationFrame` nằm trong `showScreen`).
   *
   * ⚠️ `ARENA_TICK.matchSeconds` được truyền TƯỜNG MINH: `ArenaWorld` có mặc định, nhưng truyền
   * thẳng từ hằng số thì đổi nhịp trận chỉ phải sửa MỘT chỗ (`data/arena.ts`).
   *
   * ⚠️ `heroClasses` lấy từ `this.arenaHeroClasses()` — HỢP của tiến trình TD (`meta.unlocked`) và
   * phần mở riêng của Arena, đã lọc id hợp lệ. Đưa id lạ vào đây là `summonHero` của sim tra
   * `CLASSES[id].base` ⇒ `TypeError`.
   */
  startArenaFromHome(): void {
    const scene = this.arena();
    if (!scene || !this.hud.arenaHud) {
      this.hud.toast(t('Chế độ Arena chưa sẵn sàng', 'Arena mode is not ready'));
      return;
    }
    /*
     * ⚠️⚠️ ĐƯA TRẬN TD VÀO `sleep()` — KHÔNG `stop()`, KHÔNG `pause()`. BA LỰA CHỌN, HAI CÁI SAI:
     *
     *   · `pause('arena')` — SAI: scene vẫn nằm trong danh sách RENDER, nên lính/ Nhà của trận TD vẫn
     *     được vẽ chồng dưới sân Arena, và nó vẫn nhận `pointerdown` trên MỘT canvas ⇒ cú bấm chọn
     *     land đồng thời ra lệnh di chuyển cho tướng TD.
     *   · `stop('arena')`   — SAI, và đây là lỗi ĐÃ ĐO ĐƯỢC TRONG CHROME THẬT: `stop()` bắn `SHUTDOWN`,
     *     Phaser huỷ camera/ display list của scene. NHƯNG `ArenaScene.create()` (dòng ~69, code CÓ
     *     TRƯỚC task-5, NGOÀI write scope) đăng ký một listener VÔ DANH:
     *         `this.scale.on('resize', () => this.cameras.main.setViewport(...))`
     *     — vô danh nên KHÔNG BAO GIỜ được gỡ, và `ArenaScene` không hề có nhánh `SHUTDOWN` để dọn.
     *     Sau `stop()`, mỗi lần `Hud.showScreen` chạy vòng poll `game.resizeArena()` (phát `resize` +
     *     `scale.refresh()`) là listener đó chạy trên `cameras.main` đã bị huỷ ⇒
     *     `TypeError: Cannot read properties of undefined (reading 'setViewport')` — ĐO ĐƯỢC 6–12 lần
     *     mỗi lần vào Arena, và vì exception ném TRONG `scale.refresh()` nên nó làm HỎNG luôn nhịp
     *     đo lại kích thước của Phaser.
     *   · `sleep('arena')`  — ĐÚNG: scene ngủ thì `update` VÀ `render` đều KHÔNG chạy (hết chồng hình,
     *     hết tranh `pointerdown`), nhưng scene KHÔNG bị shutdown ⇒ camera/ display list còn nguyên,
     *     listener `resize` cũ không chạm vào thứ đã huỷ, và quay lại trận TD chỉ cần `wake()`.
     *
     * ⚠️ Vì sao KHÔNG sửa `arena-scene.ts` cho gọn: người dùng đã nhấn mạnh Arena là BỔ SUNG, và file
     * đó là trận 10-wave ĐANG CHẠY PRODUCTION — nằm NGOÀI write scope của task-5. `sleep()` giải quyết
     * đúng vấn đề mà KHÔNG phải chạm vào chế độ đang ổn.
     */
    if (this.phaser.scene.isActive('arena')) this.phaser.scene.sleep('arena');

    const world = new ArenaWorld({
      // seed theo thời gian: mỗi trận Arena khác nhau, nhưng vẫn truyền được seed cố định khi test
      seed: Date.now() % 1_000_000,
      phase: 'pve',
      heroClasses: this.arenaHeroClasses(),
      matchSeconds: ARENA_TICK.matchSeconds,
    });

    this.hud.showScreen('arena');
    /*
     * ⚠️⚠️ `scene.start()` PHẢI CHẠY LẠI MỖI LẦN VÀO — LỖI ĐÃ ĐO ĐƯỢC TRONG CHROME THẬT.
     *
     * Bản đầu tôi viết `if (!isActive) scene.start(...)` rồi `scene.attachWorld(...)`. Vào Arena lần
     * ĐẦU thì đúng, nhưng lần THỨ HAI thì scene đã bị `exitArena()` gọi `stop()` ⇒ `SHUTDOWN` chạy
     * `ArenaBattleScene.dispose()`, đặt `disposed = true` và huỷ `view`/`cam`/`units`.
     * `dispose()` KHÔNG được reset lại (đó là code của task-4, NGOÀI write scope của task-5) ⇒ mọi
     * guard `if (this.disposed) return` chặn sạch: `attachWorld` gọi `unitAdapter.reset()` trên
     * adapter đã huỷ, và `cam.followSide(...)` trên camera đã destroy.
     * ĐO ĐƯỢC: 6 lần `pageerror: Cannot read properties of undefined (reading 'setViewport')`, và
     * HUD có nút nhưng KHÔNG BẤM ĐƯỢC (Playwright: "element is not visible").
     *
     * Cách đúng — KHÔNG sửa file của task-4 (ngoài write scope): `Phaser.Scene.stop()` đưa scene về
     * ngủ và `start()` chạy lại `create()` TỪ ĐẦU, tức dựng lại `view`/`cam`/`units`/`disposed`.
     * Vì vậy LUÔN `start()` rồi mới gắn trận, KHÔNG BAO GIỜ gắn vào một scene đã stop.
     *
     * ⚠️ Và `start()` phải đứng TRƯỚC `attachWorld`: `create()` mới là chỗ dựng `cam`/`view`; gọi
     * `attachWorld` trước là chạm vào `this.cam` còn `undefined` ⇒ `TypeError` ngay.
     */
    /*
     * Lần ĐẦU: `start()` để `create()` dựng camera/ lớp vẽ. Các lần SAU: scene đang `sleep`, chỉ cần
     * `wake()` — dựng lại từ đầu là phí và làm rò texture (xem `ArenaBattleView.dispose`).
     */
    if (this.phaser.scene.isSleeping('arena-battle')) this.phaser.scene.wake('arena-battle');
    else this.phaser.scene.start('arena-battle');
    scene.attachWorld(world);
    /*
     * ⚠️⚠️ ẢNH AI CHO ARENA — LỖI NGƯỜI DÙNG BÁO KHI CHƠI THỬ: *"nhân vật chưa lấy từ ảnh mà đang
     * là svg, có vẻ giống bản demo hơn là bản chơi được"*.
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: art AI được nạp bằng `this.scene.units.preloadAll()` — CHỈ cho instance
     * `UnitViews` của scene 10-wave. `ArenaBattleScene` tạo `new UnitViews(this)` là instance RIÊNG
     * và KHÔNG BAO GIỜ gọi `preloadAll()`. Số đo trong Chrome thật:
     *     chế độ 10-wave : aiLoaded = 5, enemyAiLoaded = 8
     *     Arena          : aiLoaded = 0, enemyAiLoaded = 0   ⇒ mọi nhân vật là hình VECTOR
     *
     * Nay `preloadAll()` đọc/ ghi **kho ảnh AI dùng chung** (xem `sharedAiParts` trong
     * `render/unitview.ts`) nên lần gọi này gần như tức thì: mọi mảnh đã nạp lúc boot được DÙNG LẠI,
     * KHÔNG vẽ lại vào atlas dùng chung (vẽ lại là phình gấp đôi atlas 3072² ⇒ nguy cơ đầy ⇒ vỡ hình).
     *
     * ⚠️ VÌ SAO KHÔNG SỢ CHẠY ĐUA VỚI LẦN NẠP LÚC BOOT: màn hình chờ giữ người chơi tới khi MỌI
     * `beginTask()` xong (`ui/loading.ts`: overlay chỉ ẩn khi `pending === 0`), mà art lúc boot là một
     * task (`artDone`) ⇒ không thể vào Arena khi art còn đang nạp dở.
     *
     * ⚠️ `setForms`: để dáng đang mặc (tab Trang phục) cũng hiện trong Arena — trước đây Arena không
     * gọi nên luôn dùng dáng gốc của class.
     */
    scene.units.setForms(this.meta.formByClass);
    // `reset()` sau khi nạp xong: buộc dựng lại rig ⇒ lần này dùng ảnh AI thay vì vector
    void scene.units
      .preloadAll()
      .then(() => scene.units.reset())
      .catch((err) => console.error('[arena] nạp ảnh AI thất bại — chơi bằng art vector:', err));
    scene.hooks = {
      /*
       * ⚠️ SỰ KIỆN SIM → HUD. Chỉ cập nhật nhịp vẽ, KHÔNG ghi vào `meta` ở đây: ghi save trong
       * callback chạy 30 lần/giây là mở đường cho hàng trăm lượt `localStorage.setItem` mỗi trận.
       * Việc ghi save chỉ xảy ra ở `onStatusChange` khi trận KẾT THÚC (dưới).
       * ⚠️ HOOK BÁO "ĐỔI LAND" ĐÃ BỊ XOÁ cùng khái niệm "chọn land" (xem `ArenaBattleHooks`): không còn
       * land để chọn thì không còn sự kiện đổi land. Nhờ vậy cũng rõ hơn vì sao KHÔNG thể chỉ dựa
       * vào hook để vẽ HUD — xem chú thích ngay dưới.
       */
      onWorldEvent: () => this.renderArenaHud(),
      onStatusChange: (status) => this.onArenaStatus(status),
    };

    /*
     * ⚠️⚠️ NHỊP VẼ HUD — PHẢI CÓ, KHÔNG THỂ CHỈ DỰA VÀO `onWorldEvent`.
     *
     * LỖI ĐÃ ĐO ĐƯỢC TRONG CHROME THẬT: sim chạy đúng (`world.time` 0 → 1,97 giây, `status` chuyển
     * `prep` → `fighting`), nhưng HUD ĐỨNG IM ở `0:00` và bấm summon không thấy trừ tiền. Nguyên
     * nhân: tôi chỉ gọi `renderArenaHud()` từ hook `onWorldEvent` (và hook đổi land — hook này nay
     * đã bị xoá cùng "3 land"), mà
     * **`sim/arena/world.ts` KHÔNG hề ghi vào `world.events`** (0 chỗ `events.push`) — nên
     * `onWorldEvent` KHÔNG BAO GIỜ cháy trong trận thường. Tài nguyên đào được, đồng hồ trôi, máu
     * Nhà giảm — HUD không biết gì.
     *
     * ⚠️ Vì sao KHÔNG gọi `render()` trong `ArenaBattleScene.update`: file đó là của task-4, NGOÀI
     * write scope, và tầng scene không được biết tới DOM/HUD.
     *
     * ⚠️ Vì sao `POST_UPDATE` (sau khi sim + vẽ xong) chứ không `UPDATE`: đọc state SAU khi mô phỏng
     * của khung hình đó chạy xong ⇒ HUD luôn hiện số CỦA KHUNG HÌNH VỪA VẼ, không lệch một nhịp.
     *
     * ⚠️ Và vì sao KHÔNG gọi mỗi khung hình: HUD ghi lại `innerHTML` (dựng lại toàn bộ nút). Ở 60fps
     * đó là 60 lượt dựng DOM + 60 lượt parse mỗi giây — đúng loại chi phí mà repo đã đo và loại bỏ
     * ở `openHomeFeature` ("1 cú bấm chạy 2 lần, p95 581ms"). Chốt xuống ~10 lần/giây (mỗi 100ms):
     * nhanh hơn mắt người đọc số, mà giảm 6 lần chi phí dựng DOM.
     */
    this.arenaTick = 0;
    scene.events.on(Phaser.Scenes.Events.POST_UPDATE, this.onArenaFrame, this);

    const hud = this.hud.arenaHud;
    hud.bind(scene);
    /*
     * NÚT BACK CỦA HUD ARENA — một callback, để HUD không phải biết tới `Hud` của game TD.
     *
     * ⚠️⚠️ HỎI XÁC NHẬN KHI ĐANG CHƠI — yêu cầu người dùng: *"Nút về trang chủ thay bằng nút back,
     * khi click mà đang ingame thì hỏi có tiếp tục không giống vượt ải"*.
     *
     * Dùng LẠI `#quitConfirm` của vượt ải (`Hud.openQuitConfirm`) thay vì dựng hộp thoại thứ hai: hai
     * chế độ phải hỏi GIỐNG nhau, và một hộp thoại thứ hai là chỗ để chúng lệch nhau sau lần sửa đầu.
     *
     * ⚠️ ĐIỀU KIỆN "ĐANG CHƠI": trận CHƯA kết thúc. Đã `won`/`lost`/`draw` thì thoát thẳng — hỏi xác
     * nhận lúc trận đã xong là làm phiền người chơi mà không bảo vệ được gì (không còn tiến trình để mất).
     */
    hud.onExit = () => {
      const w = this.arena()?.world;
      const stillPlaying = !!w && w.status !== 'won' && w.status !== 'lost' && w.status !== 'draw';
      if (stillPlaying) this.hud.openQuitConfirm(() => this.exitArena());
      else this.exitArena();
    };
    /*
     * ⚙️ CÀI ĐẶT TRONG LÚC CHƠI ARENA.
     *
     * Ở màn Arena, `#btnSettingsHome` (⚙️ trang chủ) bị CSS ẩn (`#app[data-screen='arena'] #home`),
     * nên đây là ĐƯỜNG DUY NHẤT để đổi ngôn ngữ/ tắt tiếng mà không phải thoát trận. Nối vào CÙNG
     * `#settingsPanel` của `Hud` — ngôn ngữ và âm thanh vẫn một nguồn sự thật, không tạo bảng thứ hai.
     */
    hud.onSettings = () => this.hud.openSettings();
    hud.setResult(null);
    hud.show();
    this.renderArenaHud();
  }

  /**
   * ĐỔI TRẠNG THÁI TRẬN ARENA.
   *
   * ⚠️ CHỈ GHI SAVE KHI TRẬN ĐÃ KẾT THÚC (`won`/`lost`/`draw`). `onStatusChange` cũng bắn cho
   * `prep`/ `fighting`, mà ghi ở đó là mỗi lần vào trận/ tạm dừng lại cộng thêm một "trận" vào
   * tiến trình — sai một cách âm thầm. `recordArenaResult()` cũng tự chặn (xem chú thích ở
   * `sim/arena-meta.ts`), ở đây chặn thêm một lớp vì đây là nơi ĐỌC/GHI SAVE.
   */
  private onArenaStatus(status: ArenaWorld['status']): void {
    const hud = this.hud.arenaHud;
    this.renderArenaHud();
    if (status !== 'won' && status !== 'lost' && status !== 'draw') return;

    const progress = this.meta.arena;
    const seconds = this.arena()?.world?.time ?? 0;
    if (!recordArenaResult(progress, status, seconds)) return;

    /*
     * THƯỞNG — CỘNG VÀO **VÍ ARENA**, KHÔNG BAO GIỜ vào `meta.gold`.
     *
     * ⚠️ Vì sao ví riêng (xem `spendFromRunWallet()` ngay dưới file này): repo đã cố ý tách "ví
     * trận" và "ví tổng" cho game TD. Arena là chế độ riêng ⇒ thưởng của nó phải ở ví riêng, nếu
     * không thì đánh Arena thành máy in vàng cho game TD và phá cân bằng kinh tế đang chạy ổn.
     *
     * Công thức: thưởng theo THỜI GIAN SỐNG + hệ số theo kết quả. Thắng nhiều hơn thua, và luôn
     * cộng dù thua — người chơi bỏ 5 phút ra phải nhận được gì đó, nếu không họ bỏ chế độ này.
     */
    const reward = Math.max(0, Math.round(seconds)) + (status === 'won' ? 500 : status === 'draw' ? 250 : 120);
    addArenaResource(progress, reward);
    hud?.setResult({ status, reward });
    // ghi save ngay (không debounce): trận vừa kết thúc là mốc tiến trình, mất nó là mất cả trận
    this.persist();
  }

  /**
   * DỰNG ẢNH CHỤP TRẠNG THÁI ARENA CHO HUD.
   *
   * ⚠️ HUD **KHÔNG** cầm `ArenaWorld`: đây là ranh giới giữa tầng UI và tầng LUẬT (xem
   * `ArenaSnapshot`). Hàm này đọc sim rồi đưa DỮ LIỆU CHẾT cho HUD.
   *
   * ⚠️⚠️ HÀM NÀY CHỈ DỰNG THỨ HUD **THẬT SỰ VẼ** (sửa 02/10/2026). Trước đây nó còn đưa
   * `enemyResource`, `coreHp*`, `scoreLeft/ scoreRight` — nhưng sau khi người dùng yêu cầu
   * *"số điểm máu và tài nguyên địch để trên đầu nhà chính"* + *"bỏ bảng Kinh tế nhịp theo trận"* thì
   * HUD không vẽ con số nào của Nhà nữa: máu 2 Nhà + tài nguyên địch do TẦNG VẼ hiện trên đầu Nhà và nó
   * đọc thẳng `world.cores`/ `world.resource` (`updateCoreLabels`). Giữ mấy trường đó ở đây là dữ liệu
   * chết — và tệ hơn là nó gợi ý HUD vẫn hiển thị máu Nhà.
   */
  /** Bộ đếm khung hình cho nhịp vẽ HUD Arena — xem `onArenaFrame`. */
  private arenaTick = 0;

  /**
   * MỘT KHUNG HÌNH CỦA SCENE ARENA — cập nhật HUD theo nhịp ~10 lần/giây (xem `startArenaFromHome`).
   *
   * ⚠️ Kiểm `isArena` trước: `POST_UPDATE` gắn trên scene, mà scene có thể `sleep` trong lúc callback
   * còn xếp hàng; vẽ HUD khi đã rời màn Arena là ghi DOM sau lưng người chơi.
   */
  private onArenaFrame(): void {
    if (!this.hud?.isArena) return;
    if (this.arenaTick++ % 6 !== 0) return; // ~10 lần/giây ở 60fps
    this.renderArenaHud();
  }

  renderArenaHud(): void {
    const hud = this.hud?.arenaHud;
    if (!hud) return;
    const w = this.arena()?.world;
    if (!w) {
      hud.render(null);
      return;
    }
    const snapshot: ArenaSnapshot = {
      resource: w.resource.left,
      seconds: w.time,
      matchSeconds: ARENA_TICK.matchSeconds,
      status: w.status,
      heroClasses: this.arenaHeroClasses(),
      /*
       * ⚠️ GIÁ NÔNG DÂN HIỆN TẠI — người dùng: *"Avatar Nông dân chưa hiển thị số tiền như các nhân
       * vật khác"*. Giá này TĂNG theo số nông dân đang sống, nên HUD không thể tự tính và cũng KHÔNG
       * được tự tính (HUD là tầng hiển thị, không phải tầng luật). Sim là nơi duy nhất biết
       * (`ArenaWorld.farmerCost`), và `buyFarmer` gọi CHÍNH hàm đó ⇒ số trên avatar luôn khớp số
       * thật sự bị trừ. Đây là "dữ liệu chết" như mọi trường khác của ảnh chụp.
       */
      farmerCost: w.farmerCost('left'),
      /*
       * ⚠️ SỐ NÔNG DÂN ĐANG SỐNG + TRẦN — yêu cầu người dùng 02/10/2026: *"để max mua được 4 farmer"*.
       * HUD cần hai số này để KHOÁ avatar nông dân và nói đúng lý do; trần lấy từ hằng số của sim
       * (`ARENA_MAX_FARMERS`, nguồn sự thật duy nhất — `buyFarmer` cũng đọc chính nó), KHÔNG chép số
       * vào HUD. Đây là "dữ liệu chết" như mọi trường khác của ảnh chụp.
       */
      farmerCount: w.units.filter((u) => u.alive && u.isFarmer && u.side === 'left').length,
      farmerMax: ARENA_MAX_FARMERS,
      /*
       * ⚠️ `heroSkills` ĐÃ BỊ XOÁ (03/10/2026) — ĐỪNG DỰNG LẠI NẾU HUD CHƯA CÓ CHỖ VẼ.
       * Người dùng: *"Bỏ thẻ thông báo skill đang dùng, skill available"* ⇒ dải chiêu
       * (`.arena-skill-strip`) trong `ui/hud-arena.ts` bị xoá hẳn, nên đoạn dựng "chiêu kế tiếp + đếm
       * ngược + tên chiêu vừa niệm" ở đây thành dữ liệu chết. Chiêu vẫn auto-niệm như cũ; chỉ phần HIỂN
       * THỊ bị bỏ. Muốn khôi phục thì phải làm CẢ HAI đầu cùng lúc (trường trong `ArenaSnapshot` + chỗ vẽ).
       */
    };
    this.hud.arenaHudSnapshot = snapshot;
    hud.render(snapshot);
  }

  /**
   * RỜI ARENA VỀ TRANG CHỦ.
   *
   * ⚠️ DỌN THEO ĐÚNG THỨ TỰ: dừng scene (giải phóng sprite/ texture — `ArenaBattleScene.dispose`
   * lo phần của nó) rồi mới đổi màn hình. Đổi màn trước thì trong lúc scene đang shutdown, HUD đã
   * bị ẩn và người chơi thấy một nhịp sân trống.
   *
   * ⚠️ KHÔNG `dispose()` HUD Arena: nó là DOM dùng lại cho trận sau (`bind()` nhận host mới). Gọi
   * `dispose()` ở đây thì lần vào Arena thứ hai sẽ không còn listener ⇒ MỌI NÚT CHẾT.
   */
  exitArena(): void {
    /*
     * ⚠️ `sleep('arena-battle')` — KHÔNG `stop()`. Cùng lý do như nhánh TD ở `startArenaFromHome`:
     * `ArenaBattleScene.dispose()` (task-4, ngoài write scope) đặt `disposed = true` và `create()`
     * KHÔNG reset cờ đó, nên `stop()` rồi vào lại là mọi guard chặn sạch (`attachWorld` chạm adapter
     * đã huỷ). `sleep()` giữ scene nguyên vẹn; lần vào sau chỉ cần `wake()`.
     *
     * ⚠️ Phải `wake('arena-battle')` khi vào lại, và `sleep` ở đây để nó ngừng vẽ sau lưng trang chủ
     * (cùng lý do `setLoopAwake(false)` — xem chú thích ở `setLoopAwake`).
     */
    /*
     * ⚠️ GỠ NHỊP VẼ THEO KHUNG HÌNH — nếu không, mỗi lần vào lại Arena là gắn THÊM một listener
     * `POST_UPDATE`: sau 5 lần vào/ ra ta có 5 listener cùng gọi `renderArenaHud()`, tức 5 lượt dựng
     * `innerHTML` cho mỗi nhịp. Đây đúng loại rò rỉ mà repo đã dính ở panel trang chủ ("bấm 1 nút
     * chạy 2 lần"), nên gỡ ở ĐÚNG chỗ đã gắn.
     */
    this.arena()?.events.off(Phaser.Scenes.Events.POST_UPDATE, this.onArenaFrame, this);
    if (this.phaser.scene.isActive('arena-battle')) this.phaser.scene.sleep('arena-battle');
    /*
     * ⚠️ XOÁ TRẠNG THÁI TRẬN Ở CẢ HAI TẦNG — yêu cầu người dùng: *"khi thoát thì ra màn hình chính và
     * clear state"*:
     *   · SCENE (`clearWorld`): world/ cổng bắt đầu/ bóng/ vệt đạn. Scene chỉ `sleep()` nên nếu không
     *     xoá, `this.arena()?.world` vẫn là trận VỪA XONG suốt lúc ở trang chủ;
     *   · HUD (`hide` + `setResult(null)`): băng kết quả, toast, và lớp `ArenaStageFx` (sống ngoài
     *     `#arenaHud` nên `hidden` của root KHÔNG che nó — đã xử trong `ArenaHud.hide`).
     */
    this.arena()?.clearWorld();
    this.hud.arenaHud?.hide();
    this.hud.arenaHud?.setResult(null);
    this.hud.arenaHudSnapshot = null;
    this.hud.showScreen('home');
  }

  /**
   * ĐỔI NGÔN NGỮ (nút VI/ EN trên thanh HUD hoặc `setLanguage`).
   *
   * `Hud` đã tự nghe `onLangChange` để `render()` lại; ở đây lo phần KHÔNG phải DOM: nhãn tên
   * tướng/ quái và nhãn Nhà do Phaser vẽ (`ArenaScene.refreshLang`).
   */
  setLanguage(): void {
    const scene = this.scene as { refreshLang?: () => void } | undefined;
    scene?.refreshLang?.();
  }

  toggleAutoNextWave(): void {
    this.autoNextWave = !this.autoNextWave;
    this.savePrefs();
    if (!this.autoNextWave) {
      this.cancelCountdown();
    } else if (this.world?.status === 'waveClear') {
      /*
       * Bật auto KHI ĐANG Ở MÀN NGHỈ (giữa 2 wave) thì phải đếm lùi NGAY.
       * Nếu chỉ chờ `onStatusChange('waveClear')` thì lần đổi trạng thái đó đã xảy ra
       * TRƯỚC khi bật ⇒ công tắc bật mà không có gì xảy ra (đã dính khi chạy verify-web).
       */
      this.scheduleNextWave();
    }
    this.hud.render();
  }

  toggleAutoSkill(): void {
    this.autoSkill = !this.autoSkill;
    this.savePrefs();
    if (this.world) this.world.autoCast = this.autoSkill;
    this.hud.render();
  }

  /** Huỷ đếm lùi tự vào wave (người chơi bấm tay, đổi màn, thua…). */
  cancelCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.startCountdown = null;
  }

  /**
   * Hết wave ⇒ nếu bật "auto wave" thì đếm lùi 3 giây rồi tự vào wave kế.
   * Đếm bằng `setInterval` 1 giây để HUD hiện số giây còn lại (người chơi thấy sắp vào
   * wave, còn kịp mua đồ hoặc tắt auto).
   */
  private scheduleNextWave(): void {
    this.cancelCountdown();
    if (!this.autoNextWave || !this.world?.canStartWave()) return;
    this.startCountdown = 3;
    this.countdownTimer = window.setInterval(() => {
      if (!this.world?.canStartWave() || !this.autoNextWave) {
        this.cancelCountdown();
        this.hud.render();
        return;
      }
      this.startCountdown = (this.startCountdown ?? 1) - 1;
      if (this.startCountdown <= 0) {
        this.cancelCountdown();
        this.startWave();
        return;
      }
      this.hud.render();
    }, 1000);
  }

  /** Bộ 7 chiêu đang trang bị của từng class — nguồn cho `World`. */
  /**
   * BỘ CHIÊU ĐÃ TRANG BỊ của 1 class (đúng thứ tự ô).
   *
   * Người dùng: *"Chỉ skill nào trang bị mới xuất hiện thôi chứ?"* — UI trong trận PHẢI dùng hàm
   * này, KHÔNG dùng `CLASSES[c].skills` (đó là danh mục 7 chiêu gốc, không phải bộ đang mang).
   */
  equippedSkillsOf(classId: ClassId): SkillDef[] {
    return equippedSkills(this.meta, classId);
  }

  skillLoadoutOf(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const c of CLASS_ORDER) out[c] = equippedSkills(this.meta, c).map((sk) => sk.id);
    return out;
  }

  /**
   * CHỌN TƯỚNG BẰNG CÁCH BẤM TRỰC TIẾP TRÊN SÂN.
   *
   * Tìm `HeroRecord` tương ứng với đơn vị vừa bấm (theo thứ tự đội hình — `lineup` và
   * `world.units` cùng thứ tự vì `scene.units.setLineup()` dựng theo đúng mảng đó), rồi đặt
   * `selectedHeroId`. Nhờ vậy thanh bên dưới tự sáng đúng thẻ tướng.
   */
  pickHeroOnField(unitId: number): boolean {
    const w = this.world;
    if (!w) return false;
    const unit = w.units.find((u) => u.id === unitId && u.team === 'hero');
    if (!unit) return false;
    /*
     * CHỌN THEO ĐƠN VỊ TRÊN SÂN — v7 không còn "sổ tướng" để tra, và đây cũng là cách ĐÚNG hơn:
     * khoá chọn là `heroId` của chính đơn vị vừa bấm, nên 2 tướng cùng class không bao giờ lẫn
     * nhau (lỗi cũ: bấm con thứ hai mà con thoi chỉ con thứ nhất — xem lịch sử `unitForHero`).
     * Bỏ qua đệ tử của Đẻ mướn: chúng thuộc team hero nhưng `isClassHero()` = false ⇒ không có
     * `heroId` gắn với người chơi.
     */
    const heroId = this.heroIdOfUnit(unitId);
    if (!heroId) return false;
    this.selectedHeroId = heroId;
    /*
     * KHÔNG toast khi chọn tướng. Yêu cầu người dùng: "Bỏ thông báo action trên màn hình
     * gameplay" — con thoi trên đầu + thẻ tướng sáng là đủ để biết đã chọn ai; toast chỉ che
     * tầm nhìn giữa trận.
     */
    this.hud.render();
    return true;
  }

  /**
   * ĐỘI HÌNH VÀO TRẬN — nay suy từ CLASS ĐANG MỞ, không còn sổ tướng.
   *
   * ⚠️ ĐỔI Ở v7: trước lấy `meta.heroes.slice(0, 5)`. Sổ đã xoá ⇒ lấy 5 class đầu trong
   * `meta.unlocked` (5 class gốc mở sẵn nên mọi người chơi đều có đội hình đầy đủ ngay).
   * Màn LUÔN bắt đầu với SÂN TRỐNG (`beginRun` → `startLevel`), danh sách này chỉ là "danh sách
   * class được phép mua trong trận".
   */
  buildLineup(): LineupEntry[] {
    return this.meta.unlocked.slice(0, MAX_HEROES_ON_FIELD).map((classId, i) => ({
      heroId: `lineup-${classId}-${i}`,
      classId,
      name: CLASSES[classId]?.name ?? classId,
      stats: classStatsFor(classId, this.meta),
    }));
  }

  /**
   * ÉP PHASER ĐO LẠI KHUNG CHỨA SÂN ĐẤU.
   *
   * ⚠️ VÌ SAO CẦN: lúc boot `#home` đang hiện nên `#arena` có kích thước **0×0**; Phaser đo
   * được 0 nên đặt canvas `width: 0px; height: 0px` (đo được: canvas attr 1000×600 nhưng CSS
   * 0×0, `scale.displaySize = 0×0`). Cửa sổ không đổi kích thước nên Phaser KHÔNG tự đo lại
   * ⇒ vào trận sân trống và MỌI CÚ BẤM TRÊN SÂN KHÔNG ĂN.
   *
   * `scene.scale.refresh()` KHÔNG đủ (đã thử): nó chỉ tính lại nội bộ, không ghi lại kích
   * thước CSS. Phải dùng `game.scale.resize()` của chính Phaser với số đo khung chứa THẬT.
   */
  resizeArena(): boolean {
    const host = document.getElementById('arena');
    if (!host) return false;
    const w = Math.max(1, Math.round(host.clientWidth));
    const h = Math.max(1, Math.round(host.clientHeight));
    if (w <= 1 || h <= 1) return false; // khung còn ẩn ⇒ chưa đo được, lần sau
    /*
     * ⚠️ KHÔNG gọi `scale.resize(containerW, containerH)`!
     *
     * Đã thử và nó SAI: `resize()` đổi luôn `baseSize` của game từ cỡ thiết kế 1000×600 thành
     * cỡ khung chứa (1576×404) ⇒ thế giới bị kéo méo và `getWorldPoint()` quy đổi sai
     * (đo được: bấm đúng toạ độ (500,450) lại ra lệnh tới (787,303)).
     *
     * Việc cần làm chỉ là BẢO PHASER ĐO LẠI KHUNG CHỨA để nó tính lại `displaySize` cho chế độ
     * FIT (lúc boot khung `#arena` bị ẩn nên `displaySize` = 0×0 ⇒ canvas CSS 0×0). Cách đúng:
     * phát sự kiện `resize` của cửa sổ rồi `refresh()` — giữ nguyên cỡ thiết kế.
     */
    const scale = this.phaser.scale as Phaser.Scale.ScaleManager & { refresh?: () => void };
    void w;
    void h;
    window.dispatchEvent(new Event('resize'));
    scale.refresh?.();
    return true;
  }

  /**
   * NGỦ/ ĐÁNH THỨC VÒNG LẶP PHASER.
   *
   * ⚠️ ĐÂY LÀ NGUYÊN NHÂN CHÍNH CỦA "KHỰNG HÌNH, ĐƠ" (người dùng báo khi miết nhanh ở trang chủ).
   *
   * ĐO ĐƯỢC (Chrome, 390×844, miết 40 lượt, đo từng khung hình):
   *   · để vòng lặp chạy:        p50 69ms · p95 210ms · max 2792ms   ← khựng rõ
   *   · NGỦ vòng lặp Phaser:     p50 35ms · p95  83ms · max   93ms   ← mượt
   *
   * Vì sao: ở TRANG CHỦ thì sân đấu bị `display: none` nhưng Phaser VẪN vẽ 30 khung/giây
   * (cập nhật rig, sprite, hiệu ứng) — công việc hoàn toàn vô ích vì người chơi không nhìn thấy,
   * mà lại tranh GPU/ CPU với thao tác TRƯỢT của trang chủ ⇒ khựng.
   * Ngủ vòng lặp khi ở nhà, đánh thức khi vào trận.
   */
  setLoopAwake(awake: boolean): void {
    const loop = this.phaser?.loop as (Phaser.Core.TimeStep & { sleep?: () => void; wake?: () => void }) | undefined;
    if (!loop) return;
    // `running` là cờ Phaser bật/tắt khi vòng lặp bị cho ngủ (xem TimeStep trong phaser.d.ts)
    const running = (loop as unknown as { running?: boolean }).running;
    if (awake) {
      // chốt trạng thái đang chờ TRƯỚC khi vòng lặp chạy lại (xem `flushStatusChange`)
      this.scene?.flushStatusChange();
      if (!running) loop.resume();
      loop.wake?.();
    } else {
      loop.pause();
      loop.sleep?.();
    }
  }

  /** Màn đang chọn (đã kiểm hợp lệ, luôn trả về 1 MapDef). */
  currentMapDef() {
    return mapById(this.meta.currentMap) ?? mapById(FIRST_MAP_ID)!;
  }

  /** Độ khó đang chọn. */
  currentDiffDef() {
    return DIFFICULTIES.find((d) => d.id === this.meta.currentDiff) ?? DIFFICULTIES[1];
  }

  /** Lưới 45 ô (15 màn × 3 độ khó) cho màn hình chọn màn. */
  mapGrid(): MapProgress[] {
    return progressGrid(this.meta);
  }

  /**
   * CHỌN MÀN + ĐỘ KHÓ rồi vào trận.
   * Luật mở khoá nằm ở `sim/progress.ts` (một bản duy nhất) — UI không tự kiểm lại.
   */
  selectMap(mapId: string, diff: Difficulty): boolean {
    const cell = this.mapGrid().find((c) => c.map.id === mapId && c.difficulty === diff);
    if (!cell?.unlocked) {
      this.hud.toast(t('Màn này còn khoá — qua màn trước đã', 'This stage is locked — clear the previous one first'));
      return false;
    }
    this.meta.currentMap = mapId;
    this.meta.currentDiff = diff;
    this.meta.stageIndex = cell.map.stageIndex;
    this.persist();
    return true;
  }

  startLevel(): void {
    this.cancelCountdown(); // sang màn mới thì bỏ mọi đếm lùi cũ
    /*
     * ⚠️ CHỐT TRẠNG THÁI CỦA TRẬN CŨ TRƯỚC KHI THAY BẰNG TRẬN MỚI.
     * Nếu trận cũ đã `won` trong lúc ở trang chủ (vòng lặp ngủ) thì phần thưởng còn đang chờ;
     * không chốt ở đây là nó sẽ rơi vào lúc bắt đầu trận mới (xem `ArenaScene.flushStatusChange`).
     */
    this.scene?.flushStatusChange();
    /*
     * ⚠️ MỖI TRẬN BẮT ĐẦU VỚI SÂN TRỐNG.
     *
     * Người dùng: "cứ click vào trận, không giữ nhân vật ở game trước… số character bằng 0, phải
     * mua ít nhất 1 character mới bắt đầu màn chơi được".
     *
     * Nghĩa là: danh sách tướng đã mua (vĩnh viễn) VẪN CÒN ở trang chủ, nhưng KHÔNG tự ra sân
     * đầu trận — người chơi phải dùng 300 vàng đầu trận để mua tướng cho CHÍNH trận này.
     * Nhờ vậy `world` khởi tạo với `lineup` RỖNG rồi mua dần qua `doBuyHero` → `addHeroToBattle`.
     */
    const lineup: LineupEntry[] = [];
    const map = this.currentMapDef();
    const diff = this.currentDiffDef();
    // màn quyết định `stageIndex` (nền độ khó), độ khó nhân thêm máu/ sát thương/ vàng
    this.meta.stageIndex = map.stageIndex;
    const world = new World({
      seed: Math.floor(Date.now() % 100000) + this.meta.stagesCleared * 977,
      stageIndex: map.stageIndex,
      lineup,
      // cờ auto chiêu của người chơi áp cho MỌI màn mới (xem toggleAutoSkill)
      autoCast: this.autoSkill,
      // 7 ô chiêu đang trang bị theo từng class (xem tab Kỹ năng)
      skillLoadout: this.skillLoadoutOf(),
      diffMult: { hp: diff.hpMult, atk: diff.atkMult, gold: diff.goldMult },
    });
    world.potions = { ...this.meta.potions };
    world.gold = this.meta.gold;
    this.world = world;
    /*
     * v7: màn LUÔN bắt đầu với SÂN TRỐNG (người dùng: *"khi bắt đầu vào trận, chưa có tướng"*) ⇒
     * không chọn sẵn tướng nào. Trước đây chọn `meta.heroes[0]` vì sổ có sẵn 5 con.
     */
    this.selectedHeroId = null;
    this.runHeroSeq = 0;
    /*
     * VÀO TRẬN ⇒ VÍ TRONG TRẬN BẮT ĐẦU BẰNG `START_RUN_GOLD` (300), ví TỔNG không mang vào.
     * Người dùng: "ví trong game start 300, số character bằng 0, phải mua ít nhất 1 character
     * mới bắt đầu màn chơi được".
     */
    world.gold = START_RUN_GOLD;
    world.diamonds = 0;
    /*
     * Giá tướng trong trận tính theo SỐ ĐÃ MUA TRONG TRẬN (`world.runOwnedHeroCounts()`), không
     * phải danh sách vĩnh viễn ở trang chủ — xem chú thích ở `runOwnedHeroCounts`.
     * Nên ở đây KHÔNG cần đẩy gì từ `meta` vào `World`.
     */
    this.syncFormsToViews();
    this.scene.attachWorld(world, lineup);
    /*
     * ⚠️ ĐỒNG BỘ LẠI CON THOI SAU KHI DỰNG LẠI SÂN: mỗi lần `attachWorld` là các đơn vị được
     * tạo mới ⇒ id đơn vị CŨ không còn tồn tại, con thoi sẽ biến mất nếu không cập nhật.
     */
    this.syncSelectMarker();
    this.hud.refreshAll();
    /*
     * NẠP ẢNH HEAD cho thẻ tướng ngay khi vào trận.
     * ⚠️ LỖI ĐÃ SỬA: `probeClassArt` chỉ được gọi từ panel trang chủ (Túi đồ/ Shop) ⇒ ai vào trận
     * mà chưa mở Túi đồ lần nào thì thẻ tướng hiện MẢNH GHÉP RỖNG (`head-img-empty`) suốt trận —
     * đo được `background-image: ""` cho cả 2 thẻ. Nay trận đấu tự nạp.
     */
    this.hud.warmHeroHeads();
  }

  onStatusChange(status: World['status']): void {
    if (!this.world) return;
    /*
     * ⚠️ LỖI NGƯỜI CHƠI BÁO: "khi kết thúc wave, nút Bắt đầu Wave vẫn bị disable".
     * Nguyên nhân: chỗ này chỉ xử lý `won`/`lost`, nên khi trạng thái đổi sang `waveClear`
     * thì HUD KHÔNG được vẽ lại ⇒ nút còn nguyên chữ "Đang đánh…" và cờ `disabled`.
     * Sửa: mọi thay đổi trạng thái đều vẽ lại HUD.
     */
    this.hud.render();
    if (status === 'waveClear') this.scheduleNextWave();
    else this.cancelCountdown();
    if (status === 'won') {
      const map = this.currentMapDef();
      const diff = this.currentDiffDef();
      this.meta.stagesCleared += 1;
      const bonus = 500 * (1 + 0.5 * (map.stageIndex / 5));
      this.world.gold += bonus;
      this.meta.bestWave = 10;
      /*
       * QUA MÀN ⇒ CHỈ LÚC NÀY TIỀN TRONG TRẬN MỚI CHẢY VÀO VÍ TỔNG.
       * Đây là điểm duy nhất cộng vàng từ trận vào ví tổng; thua thì không đi qua đây nên mất.
       */
      /*
       * QUA ẢI ⇒ CỘNG PHẦN DƯ CỦA VÍ TRẬN VÀO VÍ TỔNG.
       * Người dùng: "phần dư sau khi qua ải được cộng vào tổng".
       * Ở đây `meta.gold` ĐANG LÀ ví tổng (mọi thao tác mua trong trận đều trả lại ví tổng sau
       * khi xong), nên cộng thẳng vào `meta.gold` rồi đồng bộ `goldTotal`.
       */
      this.meta.gold += this.world.gold;
      /*
       * THƯỞNG QUA MÀN — mảnh nhân vật/ chiêu + kim cương, và tỉ lệ RẤT THẤP ra đồ
       * hoàn chỉnh. Toàn bộ bảng số + luật quay nằm ở `data/maps.ts` (thuần, test được);
       * ở đây chỉ: ghi màn đã qua → quay → cộng vào két.
       *
       * ⚠️ Dùng `Math.random` cho phần quay là SAI: `rollRewards` nhận `rng` để test và
       * chạy lại ra y hệt. Ở đây tem thời gian là đủ ngẫu nhiên cho 1 lần người chơi bấm.
       */
      const firstTime = !this.meta.clearedMaps.includes(mapKey(map.id, diff.id));
      markCleared(this.meta, map.id, diff.id);
      const spec = rewardFor(map, diff.id);
      let seed = (Date.now() % 2147483647) + map.stageIndex * 7919 + this.meta.diamonds * 31;
      const rng = () => {
        seed = (seed * 1103515245 + 12345) % 2147483648;
        return seed / 2147483648;
      };
      const roll = rollRewards(spec, rng);
      // đích rơi mảnh: class nào · chiêu nào (data-driven, có thể chỉnh)
      const targets = pickShardTargets(
        {
          classes: CLASS_ORDER.slice(),
          skills: Object.fromEntries(CLASS_ORDER.map((c) => [c, this.meta.craftedSkills[c] ?? []])),
        },
        roll,
        rng,
      );
      /*
       * ⚠️ KIM CƯƠNG CŨNG LÀ "PHẦN THƯỞNG PHẢI VƯỢT MÀN MỚI CÓ".
       * Người dùng xác nhận: "phần thưởng trong màn chơi muốn có phải vượt màn mới được".
       * `applyReward` cộng thẳng vào `meta` (ví tổng) — ở đây nó chỉ chạy SAU KHI đã thắng màn
       * nên vẫn đúng luật; nhưng để nhất quán về mô hình, kim cương thưởng được ghi nhận vào
       * ví trong trận TRƯỚC rồi mới cộng vào ví tổng cùng vàng.
       */
      this.world.diamonds += roll.diamonds;
      /*
       * ⚠️ KHÔNG cộng thêm `world.diamonds` vào `meta.diamonds` ở đây!
       * `applyReward()` ĐÃ cộng `roll.diamonds` vào `state.diamonds` (xem `sim/progress.ts:128`).
       * Cộng thêm lần nữa là THƯỞNG GẤP ĐÔI — và chỉ chạy được một lần thì không ai thấy, nên
       * rất dễ lọt. `world.diamonds` ở đây chỉ để HIỂN THỊ trong trận (và để mô hình nhất quán
       * với vàng: ví trong trận có kim cương riêng, khởi tạo 0).
       */
      applyReward(this.meta, roll, targets);
      this.persist();
      const loot: string[] = [`<b>${t('+{n} kim cương', '+{n} diamonds', { n: roll.diamonds })}</b>`];
      if (roll.heroShards > 0)
        loot.push(
          `${t('{n} mảnh nhân vật', '{n} hero shards', { n: roll.heroShards })} ${targets.heroClass ? `(${className(targets.heroClass as ClassId)})` : ''}`,
        );
      if (roll.skillShards > 0)
        loot.push(
          `${t('{n} mảnh chiêu', '{n} skill shards', { n: roll.skillShards })} ${targets.skillClass ? `(${className(targets.skillClass as ClassId)})` : ''}`,
        );
      if (roll.fullItem) loot.push(`<b>${t('ĐỒ HOÀN CHỈNH: {item}', 'FULL ITEM: {item}', { item: fullItemLabel(targets.fullItemId) })}</b>`);
      const bonusLine = firstTime
        ? t('<br/>Thưởng qua màn: <b>+{n} vàng</b>.', '<br/>First-clear bonus: <b>+{n} gold</b>.', { n: Math.round(bonus) })
        : '';
      this.hud.showBanner(
        t('QUA MÀN {map} · {diff}!', 'STAGE CLEARED {map} · {diff}!', { map: mapName(map), diff: diffName(diff).toUpperCase() }),
        t('Bạn đã vượt 10 wave và hạ gục <b>{boss}</b>.{bonus}<br/>Nhận được: {loot}', 'You survived 10 waves and defeated <b>{boss}</b>.{bonus}<br/>Rewards: {loot}', {
          boss: stageBossName(),
          bonus: bonusLine,
          loot: loot.join(' · '),
        }),
        this.winButtons(),
      );
    } else if (status === 'lost') {
      this.meta.bestWave = Math.max(this.meta.bestWave, this.world.wave);
      this.pullGold();
      this.persist();
      this.hud.showBanner(
        t('NHÀ ĐÃ THỦNG!', 'THE HOUSE HAS FALLEN!'),
        t('Bạn trụ được tới <b>wave {n}</b>.<br/>Vàng kiếm được vẫn giữ nguyên. Hãy vào <b>phòng tập luyện</b> hoặc mua thêm nhân vật rồi đánh lại.', 'You held until <b>wave {n}</b>.<br/>Gold you earned is kept. Hit the <b>training hall</b> or recruit more heroes, then try again.', { n: this.world.wave }),
        [
          { label: t('Đánh lại màn này', 'Retry this stage'), action: () => { this.hud.hideBanner(); this.startLevel(); this.hud.showScreen('game'); } },
          { label: t('Về trang chủ', 'Back to home'), action: () => { this.hud.hideBanner(); this.startLevel(); this.hud.goHome(); } },
        ],
      );
    }
  }

  /**
   * MÀN KẾ TIẾP Ở ĐÚNG ĐỘ KHÓ ĐANG CHƠI — nhưng CHỈ khi ô đó đã mở khoá.
   *
   * Người dùng: *"Ở màn hình qua ải phải là next hay back chứ?"* và chọn
   * *"Map kế tiếp, giữ nguyên độ khó"*.
   *
   * ⚠️ KHÔNG được trả về màn kế một cách mù quáng: luật mở khoá (`isUnlocked`) không đối xứng.
   *   · `M + easy`   mở khi `prevMap(M)` qua ở BẤT KỲ độ khó nào
   *   · `M + normal` CHỈ mở khi CHÍNH `M` đã qua ở `easy`
   *   · `M + elite`  CHỈ mở khi CHÍNH `M` đã qua ở `normal`
   * Nên khi vừa thắng `1.1·Dễ`, ô `1.2·Dễ` MỞ (đúng ý người dùng ⇒ nút Next xuất hiện).
   * Nhưng nếu đang ở `1.1·Thường` thì `1.2·Thường` VẪN KHOÁ (phải qua `1.2·Dễ` trước).
   * Trả về ô đang khoá là đưa nút dẫn tới màn hình "màn này còn khoá" — tệ hơn là không có nút.
   * Vì vậy trả `null` và để nút Next BIẾN MẤT thay vì dẫn vào ngõ cụt.
   */
  private nextStageTarget(): { mapId: string; diff: Difficulty } | null {
    const map = nextMap(this.currentMapDef().id);
    if (!map) return null; // đang ở màn cuối ⇒ không có "màn sau"
    const diff = this.currentDiffDef().id;
    if (!isUnlocked(this.meta.clearedMaps, map.id, diff)) return null;
    return { mapId: map.id, diff };
  }

  /**
   * Nút của banner QUA MÀN: [Màn tiếp theo] [Về trang chủ].
   *
   * Người dùng: *"Ở màn hình qua ải phải là next hay back chứ?"* ⇒ nút đầu tiên là ĐI TIẾP,
   * không phải thoát. Trước đây cả 2 nút đều đá về trang chủ, người chơi thắng xong bị đẩy ra
   * khỏi trận rồi phải tự tìm lại màn — không có cách nào đi thẳng sang màn kế.
   *
   * Bỏ nút "Ghép chiêu (trang chủ)" như người dùng chọn (2 nút thay vì 3 ⇒ banner gọn, ít tràn
   * trên máy thấp). Ghép chiêu vẫn vào được từ trang chủ như cũ.
   */
  private winButtons(): Array<{ label: string; action: () => void }> {
    const next = this.nextStageTarget();
    const buttons: Array<{ label: string; action: () => void }> = [];
    if (next) {
      buttons.push({
        label: t('Màn tiếp theo ›', 'Next stage ›'),
        action: () => {
          this.hud.hideBanner();
          /*
           * `selectMap` tự kiểm lại khoá (nguồn luật duy nhất) và trả `false` nếu hỏng — ở đây đã
           * lọc bằng `nextStageTarget()` rồi nên bình thường luôn `true`.
           */
          this.selectMap(next.mapId, next.diff);
          this.startLevel();
          this.hud.showScreen('game');
        },
      });
    }
    buttons.push({
      label: t('Về trang chủ', 'Back to home'),
      action: () => {
        this.hud.hideBanner();
        this.startLevel();
        this.hud.goHome();
      },
    });
    return buttons;
  }

  onWorldEvent(e: SimEvent): void {
    /*
     * CHỈ GIỮ TOAST CHO LỖI. Bỏ thông báo "xong wave"/ "wave bắt đầu": thanh trạng thái
     * (`#wavebar`) đã hiện wave/ số quái/ trạng thái, toast chỉ trùng lặp và nhấp nháy.
     */
    if (e.type === 'error' && e.text) this.hud.toast(e.text);
  }

  /**
   * MÀN HÌNH ĐẦU TIÊN = SẢNH CHỌN MÀN.
   *
   * Người dùng: "Ở màn hình bắt đầu vào trận, sẽ có hiển thị Màn 1.1, 1.2, 1.3…".
   * Vì vậy intro giờ là lưới màn × độ khó (kèm khoá tiến trình); bấm "Vào trận" trong đó
   * mới mở khoá nút Bắt đầu Wave (xem `beginRun`).
   *
   * ⚠️ Vẫn ghi mốc `__TDK_INTRO_AT__` cho công cụ kiểm chứng (verify-web đo thời gian boot).
   */
  private showIntro(): void {
    (globalThis as { __TDK_INTRO_AT__?: number }).__TDK_INTRO_AT__ = Math.round(performance.now());
    // màn đang chọn phải hợp lệ với save hiện tại (save cũ: 1.1 · easy)
    if (!mapById(this.meta.currentMap)) this.meta.currentMap = FIRST_MAP_ID;
    /*
     * MÀN HÌNH ĐẦU TIÊN = TRANG CHỦ (ngoài game). Người dùng: "Tách riêng biệt màn hình
     * trong game và màn hình ngoài game (trang chủ)".
     * Sân đấu vẫn được dựng sẵn phía sau (startLevel ở trên) để lúc bấm "Vào trận" là có
     * ngay, nhưng KHÔNG hiện ra ở trang chủ (CSS ẩn `#ingame`).
     */
    this.hud.showScreen('home');
  }

  // ------------------------------------------------------------ hành động

  startWave(): void {
    if (!this.world) return;
    // chưa bấm "Vào trận" thì không cho vào wave (nút cũng đã bị disable — chặn 2 lớp)
    if (!this.started) {
      this.hud.toast(t('Bấm "Vào trận" trước đã', 'Press "Deploy" first'));
      return;
    }
    this.cancelCountdown();
    this.world.startWave();
    this.meta.bestWave = Math.max(this.meta.bestWave, this.world.wave);
    this.hud.render();
  }

  setSpeed(s: number): void {
    if (!this.scene) return;
    this.scene.speed = s;
    this.hud.render();
  }

  togglePause(): void {
    if (!this.scene) return;
    this.scene.paused = !this.scene.paused;
    this.hud.render();
  }

  /** Người chơi click/ chạm vào sân ⇒ tướng đang chọn đi tới đó. */
  commandMove(x: number, y: number): void {
    if (!this.world) return;
    const id = this.selectedUnitId();
    if (id === null) {
      this.hud.toast(t('Chưa chọn tướng', 'No hero selected'));
      return;
    }
    /*
     * KHÔNG toast khi ra lệnh di chuyển: đây là thao tác LIÊN TỤC (người chơi bấm/ miết suốt
     * trận), toast sẽ nhấp nháy che tầm nhìn. Vòng tròn vàng tại chỗ bấm (do cảnh vẽ) đã là
     * phản hồi đủ rõ. Yêu cầu người dùng: "Bỏ thông báo action trên màn hình gameplay".
     */
    this.world.commandMove(id, x, y);
  }

  /**
   * TƯỚNG ĐANG CHỌN — nay trả về theo ĐƠN VỊ TRÊN SÂN (không còn sổ tướng để tra).
   * Dùng cho thẻ tướng/ phòng thời gian; đơn vị được tìm theo `heroId` trong `world`.
   */
  selectedHeroUnit(): { heroId: string; classId: ClassId; name: string } | undefined {
    const u = this.selectedHeroId ? this.unitForHero(this.selectedHeroId) : undefined;
    if (!u) return undefined;
    const classId = u.kind as ClassId;
    return { heroId: this.selectedHeroId as string, classId, name: CLASSES[classId]?.name ?? classId };
  }

  /** Tìm đơn vị trong trận ứng với 1 tướng trong sổ. */
  unitForHero(heroId: string): Unit | undefined {
    if (!this.world) return undefined;
    // đơn vị thứ `n` của mỗi class nhận id `run-<class>-<n+1>` lúc mua (xem `doBuyHero`)
    for (const u of this.world.units) {
      if (u.team !== 'hero' || !isClassHero(u)) continue;
      if (this.heroIdOfUnit(u.id) === heroId) return u;
    }
    return undefined;
  }

  /**
   * `heroId` CỦA MỘT ĐƠN VỊ TRÊN SÂN — suy từ (class, thứ tự trong sân), KHÔNG lưu vào sim.
   * Vì sao suy chứ không thêm trường vào `sim/types.ts`: `Unit` là dữ liệu mô phỏng, không cần
   * biết id tướng của người chơi; thứ tự `classHeroesOfClass` ổn định trong một màn.
   */
  private heroIdOfUnit(unitId: number): string | null {
    const w = this.world;
    if (!w) return null;
    const unit = w.units.find((u) => u.id === unitId && u.team === 'hero');
    if (!unit || !isClassHero(unit)) return null;
    const sameClass = w.units.filter((u) => u.team === 'hero' && u.kind === unit.kind && isClassHero(u));
    const idx = sameClass.findIndex((u) => u.id === unitId);
    return idx >= 0 ? `run-${unit.kind}-${idx + 1}` : null;
  }

  /** unit id trong sim của tướng đang chọn */
  selectedUnitId(): number | null {
    if (!this.selectedHeroId) return null;
    return this.unitForHero(this.selectedHeroId)?.id ?? null;
  }

  /**
   * MUA TƯỚNG — nay CHỈ TRONG TRẬN, đếm theo số tướng ĐANG SỐNG trên sân (người dùng chốt 2a).
   *
   * ⚠️ KHÔNG còn "sổ tướng": không ghi gì vào save. Mua = trả vàng ví TRẬN + dựng thẳng một đơn vị
   * trên sân với `heroId` MỚI (`run-<class>-<n>`). Nhờ vậy mỗi tướng có id riêng, thẻ tướng không
   * trùng, và số đếm chỉ phụ thuộc MÀN ĐANG CHƠI.
   *
   * ⚠️ GIÁ: tính theo số tướng CÙNG CLASS đã mua TRONG TRẬN (`runOwnedHeroCounts`), nên màn mới
   * luôn bắt đầu ở 200 vàng — đúng yêu cầu *"khi bắt đầu vào trận, chưa có tướng, tất cả các tướng
   * đều giá 200"*.
   */
  doBuyHero(classId: ClassId): void {
    const ownedInRun = this.world?.runOwnedHeroCounts()[classId] ?? 0;
    const fieldedCount = this.fieldedHeroCount();
    const r = this.spendFromRunWallet(() =>
      buyHero(this.meta, classId, ownedInRun, { fieldedCount }),
    );
    this.hud.toast(r.ok ? t('{name} ra sân', '{name} deployed', { name: className(classId) }) : simMsg(r.message));
    if (r.ok && this.world && this.world.classHeroes.length < MAX_HEROES_ON_FIELD) {
      const heroId = `run-${classId}-${++this.runHeroSeq}`;
      this.world.addHeroToBattle({
        heroId,
        classId,
        name: CLASSES[classId].name,
        stats: classStatsFor(classId, this.meta),
      });
      this.selectedHeroId = heroId;
      this.persist();
    }
    this.hud.render();
  }

  /**
   * BA HÀNH ĐỘNG CỦA TÚI ĐỒ — mở khoá class · nâng cấp tướng · bán mảnh.
   *
   * Mọi luật nằm ở `sim/meta.ts` (tầng sim thuần, test được bằng số); ở đây chỉ: gọi, DỊCH message
   * (tầng sim trả tiếng Việt — quy ước repo), lưu save, vẽ lại.
   */
  doUnlockClass(classId: ClassId): void {
    const r = unlockClassWithShards(this.meta, classId);
    this.hud.toast(simMsg(r.message));
    if (r.ok) {
      // mở khoá xong thì đội hình vào trận sau có thêm class này
      this.persist();
    }
    this.hud.render();
  }

  doUpgradeClass(classId: ClassId): void {
    const r = upgradeClassWithShards(this.meta, classId);
    this.hud.toast(simMsg(r.message));
    if (r.ok) {
      // chỉ số tính theo class ⇒ áp NGAY cho tướng class đó đang ở sân
      this.world?.refreshHeroStats(classId, classStatsFor(classId, this.meta));
      this.persist();
    }
    this.hud.render();
  }

  doSellShards(classId: ClassId, count: number): void {
    const r = sellClassShards(this.meta, classId, count);
    this.hud.toast(simMsg(r.message));
    if (r.ok) this.persist();
    this.hud.render();
  }

  /**
   * NGƯỜI DÙNG BẤM NÚT ULTIMATE của một tướng đang ở sân.
   *
   * Người dùng chốt: *"Nút bấm xuất hiện khi tôi click vào nhân vật, trên màn hình trò chơi hiện ra
   * skill của nhân vật đó"* ⇒ HUD gọi hàm này theo `heroId` của thẻ tướng đang chọn.
   * Luật (hồi 3 phút, đang hiệu lực, không phải tướng người chơi) nằm ở `World.castUltimate()`.
   */
  castUltimateOf(heroId: string): boolean {
    if (!this.world) return false;
    const unit = this.unitForHero(heroId);
    if (!unit) return false;
    const ok = this.world.castUltimate(unit);
    if (ok) {
      this.hud.toast(t('{name} — tuyệt kỹ!', '{name} — ultimate!', { name: className(unit.kind as ClassId) }));
      this.persist();
    } else {
      const left = Math.ceil(this.world.ultCooldownLeft(unit));
      this.hud.toast(
        this.world.ultActive(unit)
          ? t('Tuyệt kỹ đang hiệu lực', 'Ultimate already active')
          : t('Tuyệt kỹ còn hồi {n}s', 'Ultimate on cooldown — {n}s left', { n: left }),
      );
    }
    this.hud.render();
    return ok;
  }

  doTrain(classId: ClassId): void {
    const r = trainClass(this.meta, classId);
    /*
     * `trainClass()` trả message TIẾNG VIỆT (`"<class> lên cấp tập N"`, `"Không đủ vàng (cần N)"`)
     * — DỮ LIỆU của tầng sim, không nhét i18n vào `sim/` (đúng quy ước repo) ⇒ dịch tại đây.
     */
    this.hud.toast(
      r.ok
        ? t('{cls} lên cấp tập {n}', '{cls} reached training level {n}', { cls: className(classId), n: this.meta.training[classId] })
        : r.message,
    );
    if (r.ok) {
      // áp dụng NGAY cho các tướng class này đang trên sân — để người chơi thấy tác dụng
      // áp dụng NGAY cho MỌI tướng class này đang trên sân (chỉ số nay tính theo class)
      this.world?.refreshHeroStats(classId, classStatsFor(classId, this.meta));
      this.persist();
    }
    this.hud.render();
  }

  // ------------------------------------------------------------ TRANG PHỤC (dáng theo class)

  /**
   * ĐỒNG BỘ DÁNG ĐANG MẶC XUỐNG TẦNG HÌNH (nạp ảnh theo nhu cầu).
   * Gọi ở 3 chỗ: boot · vào trận · người chơi chọn dáng ở tab Trang phục.
   */
  private syncFormsToViews(): void {
    if (!this.scene) return;
    this.scene.units.setForms(this.meta.formByClass);
    void this.scene.units.ensureForms().catch((err) => console.warn('[trang phục] nạp dáng lỗi:', err));
  }

  /**
   * CHỌN DÁNG CHO 1 CLASS (tab Trang phục). `formId` rỗng ⇒ trả về DÁNG GỐC của class.
   *
   * Vì sao là `async`: ảnh dáng do người dùng thả vào thư mục, chỉ được cắt/ nạp lần đầu khi
   * cần (xem `loadAiSkillForm`) ⇒ phải chờ nạp xong mới báo kết quả và dựng lại hình.
   *
   * Áp NGAY cho tướng cùng class đang ở trên sân (không phải vào lại trận) — người dùng chọn
   * dáng ở trang chủ rồi "Vào trận" là thấy đúng dáng đó.
   */
  async doSelectForm(classId: ClassId, formId: string): Promise<void> {
    const def = skillFormById(classId, formId);
    if (formId && !def) {
      this.hud.toast(t('Không tìm thấy dáng này — ảnh có thể đã bị xoá hoặc đổi tên', 'Look not found — the image may have been deleted or renamed'));
      return;
    }
    /*
     * ⚠️ NẠP ẢNH TRƯỚC KHI LƯU. Ảnh hỏng/ thiếu ô/ atlas đầy ⇒ GIỮ NGUYÊN dáng cũ và báo lỗi.
     * Nếu lưu trước rồi mới phát hiện hỏng thì mỗi lần vào trận nhân vật lại lặng lẽ về dáng gốc
     * mà người chơi không hiểu vì sao (ảnh vẫn nằm trong thư mục y như cũ).
     */
    if (formId) {
      if (!this.scene) return;
      const set = await this.scene.units.ensureForm(classId, formId);
      if (!set) {
        const issues = this.scene.units.issuesFor(classId, formId) ?? [];
        this.hud.toast(
        t('Ảnh "{name}" không dùng được{why} — giữ dáng cũ', 'Image "{name}" cannot be used{why} — keeping the old look', {
          name: def?.name ?? formId,
          why: issues.length ? `: ${issues[0]}` : '',
        }),
      );
        this.hud.render();
        return;
      }
    }
    if (formId) this.meta.formByClass[classId] = formId;
    else delete this.meta.formByClass[classId];
    this.persist();
    if (!this.scene) return;
    this.scene.units.setForms(this.meta.formByClass);
    this.hud.toast(
      formId
        ? t('Đã mặc dáng "{form}" cho {cls}', 'Wearing look "{form}" for {cls}', { form: def?.name ?? formId, cls: className(classId) })
        : t('{cls} — về dáng gốc của class', '{cls} — back to the class default look', { cls: className(classId) }),
    );
    // dựng lại rig của các đơn vị cùng class (dáng quyết định tỉ lệ xương ⇒ không thể vá 1 mảnh)
    this.scene.units.resetClass(classId);
    this.hud.render();
  }

  /** Số ảnh dáng tìm được theo từng class (UI hiển thị "chưa có ảnh"). */
  formCounts(): Record<ClassId, number> {
    return skillFormCounts();
  }

  doBuyPotion(kind: PotionKind): void {
    const r = buyPotion(this.meta, kind, 1);
    this.hud.toast(simMsg(r.message));
    if (r.ok) {
      if (this.world) this.world.potions[kind] += 1;
      this.persist();
    }
    this.hud.render();
  }

  usePotion(kind: PotionKind): void {
    if (!this.world) return;
    const unitId = this.selectedUnitId();
    if (unitId === null) {
      this.hud.toast(t('Chưa chọn tướng', 'No hero selected'));
      return;
    }
    const r = this.world.usePotion(kind, unitId);
    this.hud.toast(simMsg(r.message));
    this.hud.render();
  }

  useSkill(index: number): void {
    if (!this.world) return;
    const unitId = this.selectedUnitId();
    if (unitId === null) {
      this.hud.toast(t('Chưa chọn tướng', 'No hero selected'));
      return;
    }
    const unit = this.world.unitByIdPublic(unitId);
    if (!unit) {
      this.hud.toast(t('Tướng này đã gục', 'This hero is down'));
      return;
    }
    this.world.castSkill(unit, index);
    this.hud.render();
  }

  buyAmmo(): void {
    if (!this.world) return;
    const unitId = this.selectedUnitId();
    if (unitId === null) return;
    const r = this.world.buyAmmo(unitId);
    this.hud.toast(simMsg(r.message));
    this.hud.render();
  }

  // ------------------------------------------------------------ phòng thời gian

  startTimeRoom(classId: ClassId, seconds: number): void {
    this.timeRoom = startTimeRoom(this.meta, classId, seconds);
    this.lastTimeRoomTick = performance.now();
    if (this.timeRoomTimer === null) {
      this.timeRoomTimer = window.setInterval(() => this.tickTimeRoom(), 100);
    }
    this.hud.toast(t('Bắt đầu tập luyện — tốn 10 vàng/giây', 'Training started — 10 gold/second'));
    this.hud.render();
  }

  stopTimeRoom(): void {
    if (this.timeRoom) {
      this.timeRoom.running = false;
      this.timeRoom.done = true;
      // message của sim là DỮ LIỆU tiếng Việt — dịch tại chỗ hiển thị (xem `renderTimeRoom`)
      this.timeRoom.message = t('Đã dừng', 'Stopped');
    }
    this.hud.toast(t('Dừng tập luyện', 'Training stopped'));
    this.persist();
    this.hud.render();
  }

  private tickTimeRoom(): void {
    if (!this.timeRoom || this.timeRoom.done) {
      if (this.timeRoomTimer !== null) {
        window.clearInterval(this.timeRoomTimer);
        this.timeRoomTimer = null;
      }
      return;
    }
    const now = performance.now();
    const dt = Math.min(0.5, (now - this.lastTimeRoomTick) / 1000);
    this.lastTimeRoomTick = now;
    this.timeRoom = stepTimeRoom(this.meta, this.timeRoom, dt);
    if (this.timeRoom.done) {
      this.hud.toast(simMsg(this.timeRoom.message));
      this.persist();
      this.hud.render();
    } else if (Math.random() < 0.15) {
      this.hud.render();
    }
  }

  // ------------------------------------------------------------ vàng & lưu

  /*
   * ⚠️⚠️ VÍ TRONG TRẬN ĐỘC LẬP VỚI VÍ TỔNG — ĐỌC KỸ TRƯỚC KHI SỬA.
   *
   * Người dùng chốt: "cứ click vào trận, không giữ nhân vật ở game trước, tiền và kim cương
   * reset về 0 và sau khi hoàn thành màn mới cộng vào số tổng đang có (không hoàn thành ải
   * không được thưởng)".
   *
   * MÔ HÌNH CŨ (SAI theo yêu cầu): `pullGold()` chép `world.gold` đè lên `meta.gold` ~8 lần/giây
   * ⇒ tiền kiếm trong trận chảy thẳng vào ví tổng NGAY LẬP TỨC, và thua giữa chừng vẫn giữ được
   * tiền. Như vậy không còn khái niệm "phải vượt màn mới có thưởng".
   *
   * MÔ HÌNH MỚI:
   *   · VÀO TRẬN: `world.gold` và `world.diamonds` = 0 (ví trong trận rỗng), ví TỔNG giữ nguyên;
   *   · TRONG TRẬN: tiền kiếm được chỉ nằm ở ví trong trận, dùng để mua tướng/ nâng cấp CHÍNH
   *     trận đó — không đụng tới ví tổng;
   *   · THẮNG MÀN: cộng `world.gold` + kim cương thưởng vào ví TỔNG;
   *   · THUA: không cộng gì (mất phần kiếm được trong trận).
   */
  pullGold(): void {
    // không còn tự động đồng bộ — xem chú thích ở trên
  }

  /**
   * ⚠️ `pushGold()` ĐÃ BỊ XOÁ — ĐỪNG THÊM LẠI.
   * Nó chép `meta.gold` (ví tổng) ĐÈ lên `world.gold` (ví trận), tức là mọi thao tác trong trận
   * lại NHẬN TIỀN TỪ VÍ TỔNG — ngược hoàn toàn với luật "trong trận tiêu tiền trong trận".
   * Đây chính là thứ làm bài kiểm mới bắt được: vào trận MỚI mà ví tổng bị cộng 2309 vàng.
   */

  /**
   * ĐANG Ở TRONG TRẬN (đã vào trận và chưa kết thúc)?
   * Dùng để quyết định mua sắm trừ VÍ TRẬN hay VÍ TỔNG.
   */
  inRun(): boolean {
    if (!this.world) return false;
    /*
     * ⚠️ PHẢI ĐANG Ở MÀN HÌNH TRONG TRẬN, không chỉ "world còn sống".
     * Về trang chủ rồi thì `world` vẫn tồn tại ở trạng thái `prep`, nên nếu chỉ xét trạng thái
     * thì `walletGold()` trả ví TRẬN trong khi người chơi đang ở nhà (đo được: về nhà mà
     * `walletGold()` = 300 thay vì ví tổng 7.777).
     */
    if (!this.hud?.onGameScreen) return false;
    return this.world.status === 'prep' || this.world.status === 'fighting' || this.world.status === 'waveClear';
  }

  /**
   * CHẠY MỘT THAO TÁC MUA SẮM TRÊN ĐÚNG VÍ.
   *
   * Người dùng chốt: *"trừ VÍ TRẬN khi đang đánh, trừ VÍ TỔNG khi ở trang chủ"*, và
   * *"khi đang chiến đấu mà có vàng hay kim cương thì sẽ được cộng vào vàng/kim cương trong
   * chiến đấu, có thể sử dụng mua tướng các thứ, phần dư sau khi qua ải được cộng vào tổng"*.
   *
   * Cách làm: các hàm mua trong `sim/meta.ts` đều đọc/ ghi `meta.gold` (thuần, đã có test).
   * Ở đây ta TẠM ĐƯA ví trận vào `meta.gold`, chạy hàm mua, rồi TRẢ PHẦN CÒN LẠI về ví trận.
   * Nhờ vậy không phải viết bản thứ hai của mọi hàm mua (dễ lệch luật), mà luật ví vẫn đúng.
   *
   * ⚠️ Phải trả lại trong `finally`: nếu hàm mua ném lỗi mà không trả, `meta.gold` sẽ giữ số của
   * ví trận và người chơi mất tiền tổng.
   */
  private spendFromRunWallet<T extends { ok: boolean }>(fn: () => T): T {
    if (!this.inRun() || !this.world) return fn();
    const runGold = this.world.gold;
    const purseBefore = this.meta.gold; // ví tổng TRƯỚC khi mượn — để trả lại nguyên vẹn
    this.meta.gold = runGold;
    let out: T;
    try {
      out = fn();
    } finally {
      // phần dư (hoặc toàn bộ nếu mua hụt) quay lại ví trận
      this.world.gold = Math.max(0, this.meta.gold);
      /*
       * Trả `meta.gold` về VÍ TỔNG. `goldTotal` không cần nữa vì `meta.gold` VỐN LÀ ví tổng giữa
       * các thao tác (chỉ bị mượn tạm trong lúc chạy hàm mua) — giữ thêm một bản sao là hai
       * nguồn sự thật và sớm muộn lệch nhau.
       */
      this.meta.gold = purseBefore;
    }
    return out;
  }

  persist(): void {
    if (this.saveTimer !== null) return;
    this.saveTimer = window.setTimeout(() => {
      this.saveTimer = null;
      this.pullGold();
      saveMeta(this.meta);
    }, 250);
  }

  hardReset(): void {
    resetSave();
    this.meta = loadMeta();
    this.startLevel();
    this.hud.toast(t('Đã xoá tiến trình', 'Progress wiped'));
    this.hud.render();
  }

  /**
   * GIÁ TƯỚNG đang áp dụng.
   *
   * Trong trận: tính theo SỐ ĐÃ MUA TRONG TRẬN (`world.heroPricesInRun()`) — người dùng chốt
   * "khi bắt đầu vào trận, chưa có tướng, tất cả các tướng đều giá 200 vì chưa được mua lần nào".
   * Ở trang chủ: tính theo danh sách tướng vĩnh viễn như cũ.
   */
  heroPriceOf(classId: ClassId): number {
    if (this.inRun() && this.world) {
      const p = this.world.heroPricesInRun().byClass[classId];
      if (typeof p === 'number') return p;
    }
    return heroPrice(this.meta, classId);
  }

  /**
   * SỐ TƯỚNG ĐANG Ở TRÊN SÂN, CÒN SỐNG, TRONG TRẬN NÀY.
   *
   * Người dùng: "sao khi bắt đầu game, tôi mua 1 tướng mà lại báo 5/5? Giới hạn 5/5 là đếm số
   * tướng trong trận và đang còn sống thôi nhé".
   *
   * ⚠️ Lỗi cũ: mọi chỗ đếm đều dùng `meta.heroes.length` — đó là DANH SÁCH TƯỚNG VĨNH VIỄN ở
   * trang chủ, không phải số tướng đang đánh. Người chơi có sẵn 5 tướng trong sổ ⇒ vào trận sân
   * trống, mua 1 tướng, mà badge vẫn hiện "5/5" và mọi nút mua bị khoá.
   *
   * Nay đếm theo `world.classHeroes` — đã lọc `team==='hero' && alive && isClassHero`, tức ĐÚNG
   * "tướng trong trận và đang còn sống" (đệ tử của Đẻ mướn không tính).
   * Ở trang chủ (không trong trận) thì trả về số tướng trong sổ, để badge ngoài trận vẫn có nghĩa.
   */
  fieldedHeroCount(): number {
    /*
     * v7: ĐẾM THEO MÀN (người dùng chốt phương án 2a: *"đếm tướng trong trận và còn sống"*).
     * Ở TRANG CHỦ không có màn nào đang chạy ⇒ trả 0 (trước đây trả `meta.heroes.length` để badge
     * trang chủ có nghĩa; sổ đã xoá nên 0 mới đúng — badge trang chủ nay hiện số class đã mở khoá).
     */
    if (this.inRun() && this.world) return this.world.classHeroes.length;
    return 0;
  }

  /**
   * DANH SÁCH TƯỚNG ĐANG Ở SÂN, kèm `heroId` của người chơi.
   *
   * v7: thẻ tướng trong HUD lấy từ ĐÂY (không còn sổ tướng). `heroId` suy theo thứ tự trong sân
   * (xem `heroIdOfUnit`) nên khớp đúng cái `doBuyHero` đã đặt.
   */
  fieldedHeroUnits(): Array<{ heroId: string; classId: ClassId; name: string }> {
    if (!this.world) return [];
    return this.world.units
      .filter((u) => u.team === 'hero' && isClassHero(u))
      .map((u) => {
        const heroId = this.heroIdOfUnit(u.id);
        const classId = u.kind as ClassId;
        return heroId ? { heroId, classId, name: CLASSES[classId]?.name ?? classId } : null;
      })
      .filter((x): x is { heroId: string; classId: ClassId; name: string } => x !== null);
  }

  /** Sân đã đủ tướng chưa (dùng CHUNG cho mọi nút mua, tránh mỗi nơi khoá một kiểu). */
  isFieldFull(): boolean {
    return this.fieldedHeroCount() >= MAX_HEROES_ON_FIELD;
  }

  /**
   * CÒN MUA ĐƯỢC NHÂN VẬT CLASS NÀY CHO TRẬN NÀY KHÔNG?
   * Dùng cho các nút mua (chiêu mộ nhanh + tab Mua tướng) — khoá nút khi: sân đầy, HAY sổ đã đủ
   * `MAX_ROSTER` mà class này không còn bản ghi nào chưa ra sân. Không có hàm này thì người chơi
   * bấm được rồi ăn toast "sổ đã đủ 5" mà không hiểu vì sao.
   */
  canRecruit(classId: ClassId): boolean {
    return this.recruitBlockReason(classId) === null;
  }

  /**
   * LÝ DO CHƯA MUA ĐƯỢC (null = mua được) — để UI/ toast nói ĐÚNG cái đang chặn.
   * Người dùng từng bị báo *"vượt 5 tướng"* trong khi thật ra SÂN đã đủ 5 tướng cũ.
   */
  recruitBlockReason(classId: ClassId): 'fieldFull' | 'locked' | 'poor' | null {
    if (this.fieldedHeroCount() >= MAX_HEROES_ON_FIELD) return 'fieldFull';
    if (!this.meta.unlocked.includes(classId)) return 'locked';
    if (this.walletGold() < heroPrice(this.meta, classId, this.world?.runOwnedHeroCounts()[classId] ?? 0)) return 'poor';
    return null;
  }

  /**
   * VÍ ĐANG DÙNG ĐỂ HIỂN THỊ/ CHI TIÊU.
   *
   * ⚠️ Đây là chỗ tôi từng để lọt: HUD trong trận vẫn đọc `meta.gold` (ví TỔNG) nên người dùng
   * báo "khi vào trận sao tôi không thấy 300 vàng?" — ví trận có 300 nhưng màn hình hiện số của
   * ví tổng. Trong trận PHẢI hiện ví TRẬN; ở trang chủ hiện ví TỔNG.
   */
  walletGold(): number {
    if (this.inRun() && this.world) return this.world.gold;
    return this.meta.gold;
  }

  walletDiamonds(): number {
    if (this.inRun() && this.world) return this.world.diamonds;
    return this.meta.diamonds ?? 0;
  }

  trainCostOf(classId: ClassId): number {
    return trainCost(this.meta, classId);
  }

  get waveLabel(): string {
    const w = this.world;
    if (!w) return '0/10';
    return `${w.wave}/${w.waves.length}`;
  }

  get className(): string {
    return CLASSES[this.shopClass].name;
  }

  get classOrder(): ClassId[] {
    return CLASS_ORDER;
  }
}
